/**
 * Uitleg per afwerkingskeuze voor het (i)-icoon op de productpagina.
 *
 * Eén centrale plek voor de klanttekst van afwerkingsopties (rillen,
 * perforeren, boren, hoeken afronden, zomen, veredelen, verpakken, ...) in
 * nl/en/de/fr. Vaste waarden staan in EXACT; waarden met een getal erin
 * (boorgaten, radius, aantal rillijnen, ringen om de N cm, ...) worden door
 * een patroonregel opgebouwd.
 *
 * Onbekende talen vallen terug op Engels. Onbekende opties of waarden geven
 * null, zodat de aanroeper zijn eigen bron kan gebruiken.
 *
 * Voorrang in de configurator (getOptionDescription): voor nl wint een
 * opgeslagen productspecifieke omschrijving, anders deze module. Voor de
 * andere talen wint deze module, omdat de opgeslagen tekst daar vaak de
 * onvertaalde Nederlandse is; een opgeslagen tekst wordt dan alleen gebruikt
 * voor waarden die hier niet beschreven staan.
 */

export type OptionDescriptionLocale = 'nl' | 'en' | 'de' | 'fr';
type Localized = Record<OptionDescriptionLocale, string>;

const LOCALES: OptionDescriptionLocale[] = ['nl', 'en', 'de', 'fr'];

function t(nl: string, en: string, de: string, fr: string): Localized {
  return { nl, en, de, fr };
}

function normalizeLocale(locale: unknown): OptionDescriptionLocale {
  const lang = String(locale || 'nl').toLowerCase().split(/[-_]/)[0];
  return (LOCALES as string[]).includes(lang) ? (lang as OptionDescriptionLocale) : 'en';
}

/** "3,5" / "6.4" -> komma voor nl/de/fr, punt voor en. */
function num(raw: string, lang: OptionDescriptionLocale): string {
  const normalized = raw.replace(',', '.');
  return lang === 'en' ? normalized : normalized.replace('.', ',');
}

const COUNT_WORDS: Record<string, number> = {
  one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7,
  eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12, thirteen: 13, fourteen: 14,
};

// ---------------------------------------------------------------------------
// Gedeelde teksten
// ---------------------------------------------------------------------------

const NO_CREASE = t(
  'Geen rillijn. Het product blijft vlak en wordt niet voorbereid op vouwen.',
  'No crease line. The product stays flat and is not prepared for folding.',
  'Keine Rilllinie. Das Produkt bleibt flach und wird nicht zum Falzen vorbereitet.',
  'Pas de rainage. Le produit reste plat et n’est pas préparé pour le pliage.',
);

const CREASED = t(
  'Gerild: er wordt een vouwlijn in het papier gedrukt, zodat het strak vouwt en de inkt op de vouw niet breekt. Aan te raden bij dikker papier.',
  'Creased: a fold line is pressed into the paper so it folds cleanly and the ink does not crack on the fold. Recommended for heavier paper.',
  'Gerillt: Eine Falzlinie wird ins Papier gedrückt, damit es sauber faltet und die Farbe am Falz nicht bricht. Empfohlen bei dickerem Papier.',
  'Rainé : une ligne de pli est marquée dans le papier pour un pliage net, sans que l’encre ne casse au pli. Conseillé pour les papiers épais.',
);

const NOT_CREASED = t(
  'Niet gerild. Kies dit als het product vlak blijft of als je zelf nabewerkt. Bij dik papier kan een vouw zonder ril barsten.',
  'Not creased. Choose this if the product stays flat or you finish it yourself. On heavier paper a fold without a crease may crack.',
  'Nicht gerillt. Wählen Sie dies, wenn das Produkt flach bleibt oder Sie es selbst weiterverarbeiten. Bei dickem Papier kann ein Falz ohne Rillung brechen.',
  'Non rainé. À choisir si le produit reste plat ou si vous le façonnez vous-même. Sur papier épais, un pli sans rainage peut se fendre.',
);

const NO_CUSTOM_SHAPE = t(
  'Geen eigen vorm. Het product blijft rechthoekig op het gekozen formaat.',
  'No custom shape. The product stays rectangular in the chosen size.',
  'Keine eigene Form. Das Produkt bleibt rechteckig im gewählten Format.',
  'Pas de forme personnalisée. Le produit reste rectangulaire au format choisi.',
);

const OWN_SHAPE = t(
  'Eigen vorm uitsnijden. Kies dit alleen als je drukbestand een duidelijke snijlijn bevat.',
  'Cut to your own shape. Only choose this if your print file contains a clear cut line.',
  'Eigene Form ausschneiden. Wählen Sie dies nur, wenn Ihre Druckdatei eine klare Schnittlinie enthält.',
  'Découpe à votre propre forme. À choisir uniquement si votre fichier contient une ligne de découpe claire.',
);

const CLEAN_CUT_YES = t(
  'Schoonsnijden: het product wordt rondom recht en netjes op het gekozen eindformaat gesneden.',
  'Trimmed: the product is cut straight and neatly to the chosen final size on all sides.',
  'Randbeschnitt: Das Produkt wird rundum gerade und sauber auf das gewählte Endformat geschnitten.',
  'Coupe franche : le produit est coupé droit et proprement au format final choisi, sur tous les côtés.',
);

const CLEAN_CUT_NO = t(
  'Niet extra schoonsnijden. Standaardafwerking zonder extra snijbewerking.',
  'No extra trimming. Standard finish without an additional cutting step.',
  'Kein zusätzlicher Randbeschnitt. Standardverarbeitung ohne weiteren Schneidevorgang.',
  'Pas de coupe supplémentaire. Finition standard sans étape de découpe en plus.',
);

const NO_PERFORATION = t(
  'Geen perforatielijn. Het product heeft geen scheurlijn.',
  'No perforation line. The product has no tear-off line.',
  'Keine Perforation. Das Produkt hat keine Abreißlinie.',
  'Pas de perforation. Le produit n’a pas de ligne détachable.',
);

const NO_DRILLHOLES = t(
  'Zonder boorgaten. Kies dit als je het product anders ophangt of bevestigt.',
  'No drill holes. Choose this if you hang or mount the product another way.',
  'Ohne Bohrlöcher. Wählen Sie dies, wenn Sie das Produkt anders aufhängen oder befestigen.',
  'Sans trous. Choisissez cette option si vous accrochez ou fixez le produit autrement.',
);

const STRAIGHT_CORNERS = t(
  'Rechte hoeken. Dit is de standaardafwerking.',
  'Square corners. This is the standard finish.',
  'Gerade Ecken. Dies ist die Standardverarbeitung.',
  'Coins droits. C’est la finition standard.',
);

const NO_FINISHING = t(
  'Geen extra veredeling. Je ontwerp wordt alleen gedrukt.',
  'No extra embellishment. Your design is printed only.',
  'Keine zusätzliche Veredelung. Ihr Design wird nur gedruckt.',
  'Pas d’ennoblissement supplémentaire. Votre design est uniquement imprimé.',
);

const NOT_SEALED = t(
  'Niet per stuk in folie geseald. De producten worden samen verpakt.',
  'Not individually sealed in film. The products are packed together.',
  'Nicht einzeln in Folie eingeschweißt. Die Produkte werden zusammen verpackt.',
  'Pas d’emballage individuel sous film. Les produits sont emballés ensemble.',
);

const PACKED = t(
  'Standaard verpakt voor verzending, zodat het product beschermd bij je aankomt.',
  'Packed as standard for shipping, so the product arrives protected.',
  'Standardmäßig für den Versand verpackt, damit das Produkt geschützt ankommt.',
  'Emballé de façon standard pour l’expédition, afin que le produit arrive protégé.',
);

const NO_FLAP = t(
  'Geen flappen aan de omslag.',
  'No fold-out flaps on the cover.',
  'Keine Klappen am Umschlag.',
  'Pas de rabats sur la couverture.',
);

const NO_LAMINATE = t(
  'Geen laminaat. De print blijft onbeschermd en heeft de uitstraling van het materiaal zelf.',
  'No laminate. The print is not protected and keeps the look of the material itself.',
  'Kein Laminat. Der Druck bleibt ungeschützt und behält die Optik des Materials.',
  'Sans pelliculage. L’impression n’est pas protégée et garde l’aspect du matériau.',
);

const GLOSS_LAMINATE = t(
  'Glanzend laminaat: een beschermende folie over de print die kleuren laat stralen en beschermt tegen krassen en vocht.',
  'Gloss laminate: a protective film over the print that makes colours shine and protects against scratches and moisture.',
  'Glanzlaminat: eine Schutzfolie über dem Druck, die Farben leuchten lässt und vor Kratzern und Feuchtigkeit schützt.',
  'Pelliculage brillant : un film protecteur sur l’impression qui fait ressortir les couleurs et protège des rayures et de l’humidité.',
);

const MATT_LAMINATE = t(
  'Mat laminaat: een beschermende folie met een rustige, niet spiegelende uitstraling. Beschermt tegen krassen en vocht.',
  'Matt laminate: a protective film with a calm, non-reflective look. Protects against scratches and moisture.',
  'Mattlaminat: eine Schutzfolie mit ruhiger, nicht spiegelnder Optik. Schützt vor Kratzern und Feuchtigkeit.',
  'Pelliculage mat : un film protecteur à l’aspect sobre, sans reflets. Protège des rayures et de l’humidité.',
);

const NOT_APPLICABLE = t(
  'Niet van toepassing voor dit product.',
  'Not applicable to this product.',
  'Für dieses Produkt nicht zutreffend.',
  'Non applicable pour ce produit.',
);

const ONE_OFF_CLIP = t(
  'Clipsluiting voor eenmalig gebruik: de clip klikt vast en gaat niet meer open zonder het bandje door te knippen. Zo is het niet over te dragen.',
  'One-time clip closure: the clip locks and cannot be opened again without cutting the band, so it cannot be passed on.',
  'Clipverschluss zur einmaligen Nutzung: Der Clip rastet ein und lässt sich nur durch Aufschneiden des Bandes öffnen. So ist es nicht übertragbar.',
  'Fermeture à clip à usage unique : le clip se verrouille et ne s’ouvre plus sans couper le bracelet. Il n’est donc pas transmissible.',
);

// ---------------------------------------------------------------------------
// Banieren, doeken en vlaggen: afwerking rondom en per zijde
// ---------------------------------------------------------------------------

const EDGE_FINISH: Record<string, Localized> = {
  none: t(
    'Zonder ringen of zoom. Kies dit als je het doek zelf klemt, plakt of in een frame gebruikt.',
    'No eyelets or hem. Choose this if you clamp, glue or frame the material yourself.',
    'Ohne Ösen oder Saum. Wählen Sie dies, wenn Sie das Material selbst klemmen, kleben oder in einem Rahmen verwenden.',
    'Sans œillets ni ourlet. À choisir si vous fixez, collez ou encadrez la bâche vous-même.',
  ),
  trimmed: t(
    'Schoonsnijden: recht afgesneden zonder ringen of zoom. Geschikt als je het doek zelf klemt, plakt of in een frame verwerkt.',
    'Trimmed: cut straight without eyelets or hem. Suitable if you clamp, glue or frame the material yourself.',
    'Randbeschnitt: gerade geschnitten ohne Ösen oder Saum. Geeignet, wenn Sie das Material selbst klemmen, kleben oder rahmen.',
    'Coupe franche : coupé droit, sans œillets ni ourlet. Adapté si vous fixez, collez ou encadrez la bâche vous-même.',
  ),
  custom_shape: t(
    'Contoursnijden: uitgesneden langs de vorm van je ontwerp. Kies dit alleen als je bestand een duidelijke snijlijn bevat.',
    'Contour cut: cut along the shape of your design. Only choose this if your file contains a clear cut line.',
    'Konturschnitt: entlang der Form Ihres Designs geschnitten. Nur wählen, wenn Ihre Datei eine klare Schnittlinie enthält.',
    'Découpe à la forme : découpé selon la forme de votre design. À choisir uniquement si votre fichier contient une ligne de découpe claire.',
  ),
  eyelet_corner: t(
    'Ringen in de hoeken. Handig voor snelle montage met touw, haakjes of tie-wraps.',
    'Eyelets in the corners. Handy for quick mounting with rope, hooks or cable ties.',
    'Ösen in den Ecken. Praktisch für eine schnelle Befestigung mit Seil, Haken oder Kabelbindern.',
    'Œillets dans les coins. Pratique pour une fixation rapide avec corde, crochets ou colliers.',
  ),
  eyelets: t(
    'Ringen in de rand, zodat je het doek met touw, haakjes of tie-wraps bevestigt.',
    'Eyelets in the edge, so you can fix the material with rope, hooks or cable ties.',
    'Ösen im Rand, damit Sie das Material mit Seil, Haken oder Kabelbindern befestigen können.',
    'Œillets dans le bord, pour fixer la bâche avec une corde, des crochets ou des colliers.',
  ),
  hemmed: t(
    'Zomen: de rand wordt omgeslagen en vastgezet. Zo is de rand sterker en netter, zonder ringen.',
    'Hemmed: the edge is folded over and secured. This makes it stronger and neater, without eyelets.',
    'Gesäumt: Der Rand wird umgeschlagen und fixiert. So ist er stabiler und sauberer, ohne Ösen.',
    'Ourlet : le bord est replié et fixé. Il est ainsi plus solide et plus net, sans œillets.',
  ),
  hemmed_1_cm: t(
    'Zomen: de rand wordt ongeveer 1 cm omgeslagen en vastgezet. Zo is de rand sterker en netter, zonder ringen.',
    'Hemmed: the edge is folded over by about 1 cm and secured. This makes it stronger and neater, without eyelets.',
    'Gesäumt: Der Rand wird etwa 1 cm umgeschlagen und fixiert. So ist er stabiler und sauberer, ohne Ösen.',
    'Ourlet : le bord est replié d’environ 1 cm et fixé. Il est ainsi plus solide et plus net, sans œillets.',
  ),
  hem_eyelet_corner: t(
    'Zomen met ringen in de hoeken. Netter en sterker dan alleen hoekringen.',
    'Hemmed with eyelets in the corners. Neater and stronger than corner eyelets alone.',
    'Gesäumt mit Ösen in den Ecken. Sauberer und stabiler als nur Eckösen.',
    'Ourlet avec œillets dans les coins. Plus net et plus solide que des œillets d’angle seuls.',
  ),
  custom_eyelets: t(
    'Ringen op eigen positie. Geef de plekken van de ringen duidelijk aan in je printbestand.',
    'Eyelets at custom positions. Clearly mark where you want them in your print file.',
    'Ösen an eigener Position. Markieren Sie die gewünschten Stellen deutlich in Ihrer Druckdatei.',
    'Œillets à la position de votre choix. Indiquez clairement leur emplacement dans votre fichier.',
  ),
  sillicone_edge: t(
    'Platte pees (14 x 3 mm): een siliconen rand waarmee je het doek strak in een spanframe drukt. Er zijn geen ringen of zoom zichtbaar.',
    'Flat silicone edge (14 x 3 mm): a silicone strip that lets you press the fabric tightly into a tension frame. No eyelets or hem are visible.',
    'Flacher Keder (14 x 3 mm): ein Silikonrand, mit dem Sie den Stoff straff in einen Spannrahmen drücken. Keine sichtbaren Ösen oder Säume.',
    'Jonc plat (14 x 3 mm) : un bord en silicone qui permet d’insérer le tissu bien tendu dans un cadre. Aucun œillet ni ourlet visible.',
  ),
  strap_eyelets_corners: t(
    'Verstevigingsband in de rand met ringen in de hoeken. De band maakt de rand extra sterk.',
    'Reinforcement strap in the edge with eyelets in the corners. The strap makes the edge extra strong.',
    'Verstärkungsband im Rand mit Ösen in den Ecken. Das Band macht den Rand besonders stabil.',
    'Sangle de renfort dans le bord avec œillets dans les coins. La sangle rend le bord très solide.',
  ),
  strap_cord_corners: t(
    'Verstevigingsband in de rand met een koord op de hoeken, waarmee je het doek vastbindt.',
    'Reinforcement strap in the edge with a cord at the corners to tie the material in place.',
    'Verstärkungsband im Rand mit einer Kordel an den Ecken, mit der Sie das Material festbinden.',
    'Sangle de renfort dans le bord avec une cordelette aux coins pour attacher la bâche.',
  ),
};

function edgePattern(value: string, lang: OptionDescriptionLocale): string | null {
  let m = value.match(/^sleeve_(\d+(?:[.,]\d+)?)_?cm$/);
  if (m) {
    const cm = num(m[1], lang);
    return t(
      `Tunnelzoom van ${cm} cm (plat gemeten): een open zoom waar je een stok, buis of spanstang doorheen schuift. Kies de maat op basis van de dikte van je stang.`,
      `Pole pocket of ${cm} cm (measured flat): an open hem to slide a pole, tube or tension rod through. Choose the size based on the thickness of your pole.`,
      `Hohlsaum von ${cm} cm (flach gemessen): ein offener Saum, durch den Sie eine Stange, ein Rohr oder eine Spannstange schieben. Wählen Sie die Größe passend zur Stangendicke.`,
      `Fourreau de ${cm} cm (mesuré à plat) : un ourlet ouvert pour glisser une barre, un tube ou une tringle. Choisissez la taille selon l’épaisseur de votre barre.`,
    )[lang];
  }
  m = value.match(/^(hem_)?eyelets_(\d+)(_stormfix)?$/);
  if (m) {
    const n = m[2];
    if (m[3]) {
      return t(
        `Zomen met ringen om de ${n} cm, en in de hoeken twee ringen (stormfix). Extra sterk voor plekken met veel wind.`,
        `Hemmed with eyelets every ${n} cm and two eyelets in each corner (stormfix). Extra strong for windy locations.`,
        `Gesäumt mit Ösen alle ${n} cm und zwei Ösen in jeder Ecke (Stormfix). Besonders stabil für windige Standorte.`,
        `Ourlet avec œillets tous les ${n} cm et deux œillets dans chaque coin (stormfix). Très solide pour les endroits venteux.`,
      )[lang];
    }
    if (m[1]) {
      return t(
        `Zomen met ringen om de ${n} cm. De zoom maakt de rand sterker, de ringen zorgen voor stevige bevestiging rondom.`,
        `Hemmed with eyelets every ${n} cm. The hem strengthens the edge and the eyelets give secure fixing points all round.`,
        `Gesäumt mit Ösen alle ${n} cm. Der Saum verstärkt den Rand, die Ösen sorgen für eine sichere Befestigung.`,
        `Ourlet avec œillets tous les ${n} cm. L’ourlet renforce le bord et les œillets offrent une fixation solide.`,
      )[lang];
    }
    return t(
      `Ringen om de ${n} cm. Hoe kleiner de afstand, hoe meer bevestigingspunten en hoe strakker het doek hangt.`,
      `Eyelets every ${n} cm. The smaller the spacing, the more fixing points and the tighter the material hangs.`,
      `Ösen alle ${n} cm. Je kleiner der Abstand, desto mehr Befestigungspunkte und desto straffer hängt das Material.`,
      `Œillets tous les ${n} cm. Plus l’écart est petit, plus il y a de points de fixation et plus la bâche est tendue.`,
    )[lang];
  }
  m = value.match(/^strap_eyelets_(\d+)$/);
  if (m) {
    const n = m[1];
    return t(
      `Verstevigingsband in de rand met ringen om de ${n} cm. Extra sterk bij grote formaten of veel spanning.`,
      `Reinforcement strap in the edge with eyelets every ${n} cm. Extra strong for large sizes or high tension.`,
      `Verstärkungsband im Rand mit Ösen alle ${n} cm. Besonders stabil bei großen Formaten oder hoher Spannung.`,
      `Sangle de renfort dans le bord avec œillets tous les ${n} cm. Très solide pour les grands formats ou une forte tension.`,
    )[lang];
  }
  return null;
}

// ---------------------------------------------------------------------------
// Veredeling: foliedruk, UV-lak, koudfolie, hotfoil
// ---------------------------------------------------------------------------

const FOIL_COLOURS: Record<string, Localized> = {
  gold: t('goud', 'gold', 'Gold', 'or'),
  silver: t('zilver', 'silver', 'Silber', 'argent'),
  rose: t('roségoud', 'rose gold', 'Roségold', 'or rose'),
  copper: t('koper', 'copper', 'Kupfer', 'cuivre'),
  glitter: t('glitter', 'glitter', 'Glitzer', 'pailleté'),
  rainbow: t('regenboog', 'rainbow', 'Regenbogen', 'arc-en-ciel'),
  red: t('rood', 'red', 'Rot', 'rouge'),
  green: t('groen', 'green', 'Grün', 'vert'),
  ocean_blue: t('oceaanblauw', 'ocean blue', 'Ozeanblau', 'bleu océan'),
  blue: t('blauw', 'blue', 'Blau', 'bleu'),
  black: t('zwart', 'black', 'Schwarz', 'noir'),
};

function foilColour(raw: string, lang: OptionDescriptionLocale): { colour: string; matt: boolean } | null {
  const matt = /_matt$/.test(raw);
  const key = raw.replace(/_matt$/, '');
  const colour = FOIL_COLOURS[key];
  return colour ? { colour: colour[lang], matt } : null;
}

const MATT_SUFFIX = t(' Met een matte metallic uitstraling.', ' With a matt metallic look.', ' Mit matter Metallic-Optik.', ' Avec un aspect métallisé mat.');

function spotFinishPattern(value: string, lang: OptionDescriptionLocale): string | null {
  let m = value.match(/^(double_sided_)?foil_([a-z_]+)$/);
  if (m) {
    const c = foilColour(m[2], lang);
    if (!c) return null;
    const both = Boolean(m[1]);
    return t(
      `Foliedruk in ${c.colour}${both ? ' aan beide zijden' : ''}: delen van je ontwerp krijgen een glanzende folielaag. Opvallend voor logo’s en tekst. Geef de foliedelen aan in je bestand.`,
      `Foil print in ${c.colour}${both ? ' on both sides' : ''}: parts of your design get a shiny foil layer. Eye-catching for logos and text. Mark the foil areas in your file.`,
      `Foliendruck in ${c.colour}${both ? ' auf beiden Seiten' : ''}: Teile Ihres Designs erhalten eine glänzende Folienschicht. Auffällig für Logos und Text. Markieren Sie die Folienbereiche in Ihrer Datei.`,
      `Marquage à chaud ${c.colour}${both ? ' recto verso' : ''} : certaines parties de votre design reçoivent une couche de film brillant. Idéal pour logos et textes. Indiquez les zones dans votre fichier.`,
    )[lang];
  }
  m = value.match(/^(partial_gloss_plus_)?(?:partial_)?spotfoil_([a-z_]+)$/);
  if (m) {
    const c = foilColour(m[2], lang);
    if (!c) return null;
    const suffix = c.matt ? MATT_SUFFIX[lang] : '';
    if (m[1]) {
      return t(
        `Hoogglans UV-lak en koudfolie in ${c.colour} op delen van je ontwerp. Een sterk contrast van glans en metallic. Geef beide vlakken aan in je bestand.${suffix}`,
        `High-gloss spot UV plus cold foil in ${c.colour} on parts of your design. A strong contrast of gloss and metallic. Mark both areas in your file.${suffix}`,
        `Hochglanz-UV-Lack plus Kaltfolie in ${c.colour} auf Teilen Ihres Designs. Starker Kontrast aus Glanz und Metallic. Markieren Sie beide Bereiche in Ihrer Datei.${suffix}`,
        `Vernis UV brillant sélectif et dorure à froid ${c.colour} sur certaines parties de votre design. Fort contraste brillant et métallisé. Indiquez les deux zones dans votre fichier.${suffix}`,
      )[lang];
    }
    return t(
      `Koudfolie in ${c.colour}: een metallic folie op delen van je ontwerp, vlak in het papier zonder voelbaar reliëf. Geef de foliedelen aan in je bestand.${suffix}`,
      `Cold foil in ${c.colour}: a metallic foil on parts of your design, flat on the paper without raised relief. Mark the foil areas in your file.${suffix}`,
      `Kaltfolie in ${c.colour}: eine Metallicfolie auf Teilen Ihres Designs, flach ohne fühlbares Relief. Markieren Sie die Folienbereiche in Ihrer Datei.${suffix}`,
      `Dorure à froid ${c.colour} : un film métallisé sur certaines parties de votre design, à plat sans relief. Indiquez les zones dans votre fichier.${suffix}`,
    )[lang];
  }
  m = value.match(/^hotfoil_([a-z_]+)$/);
  if (m) {
    const c = foilColour(m[1], lang);
    if (!c) return null;
    const suffix = c.matt ? MATT_SUFFIX[lang] : '';
    return t(
      `Hotfoil in ${c.colour}: metallic folie die met warmte en druk op delen van je ontwerp wordt aangebracht. Een luxe, licht voelbare afwerking.${suffix}`,
      `Hot foil in ${c.colour}: metallic foil applied with heat and pressure to parts of your design. A luxurious, slightly tactile finish.${suffix}`,
      `Heißfolie in ${c.colour}: Metallicfolie, die mit Wärme und Druck auf Teile Ihres Designs aufgebracht wird. Eine edle, leicht fühlbare Veredelung.${suffix}`,
      `Dorure à chaud ${c.colour} : un film métallisé appliqué par chaleur et pression sur certaines parties de votre design. Une finition luxueuse et légèrement en relief.${suffix}`,
    )[lang];
  }
  return null;
}

const SPOT_FINISH: Record<string, Localized> = {
  none: NO_FINISHING,
  embossed_partial_gloss_40: t(
    'Opdikkende hoogglans UV-lak (40 micron) op delen van je ontwerp. Je ziet en voelt het reliëf, bijvoorbeeld op een logo. Geef de lakvlakken aan in je bestand.',
    'Raised high-gloss spot UV (40 micron) on parts of your design. You can see and feel the relief, for example on a logo. Mark the varnish areas in your file.',
    'Erhabener Hochglanz-UV-Lack (40 Mikron) auf Teilen Ihres Designs. Das Relief ist sicht- und fühlbar, etwa auf einem Logo. Markieren Sie die Lackflächen in Ihrer Datei.',
    'Vernis UV brillant sélectif en relief (40 microns) sur certaines parties de votre design. Le relief se voit et se touche, par exemple sur un logo. Indiquez les zones dans votre fichier.',
  ),
  partial_gloss: t(
    'Hoogglans UV-lak op delen van je ontwerp. Geeft een mooi contrast tussen mat en glans, bijvoorbeeld op een logo. Geef de lakvlakken aan in je bestand.',
    'High-gloss spot UV on parts of your design. Creates a contrast between matt and gloss, for example on a logo. Mark the varnish areas in your file.',
    'Hochglanz-UV-Lack auf Teilen Ihres Designs. Erzeugt einen Kontrast zwischen matt und glänzend, etwa auf einem Logo. Markieren Sie die Lackflächen in Ihrer Datei.',
    'Vernis UV brillant sélectif sur certaines parties de votre design. Crée un contraste mat et brillant, par exemple sur un logo. Indiquez les zones dans votre fichier.',
  ),
  double_sided_partial_gloss: t(
    'Hoogglans UV-lak op delen van je ontwerp, aan beide zijden. Geef de lakvlakken per zijde aan in je bestand.',
    'High-gloss spot UV on parts of your design, on both sides. Mark the varnish areas for each side in your file.',
    'Hochglanz-UV-Lack auf Teilen Ihres Designs, auf beiden Seiten. Markieren Sie die Lackflächen je Seite in Ihrer Datei.',
    'Vernis UV brillant sélectif sur certaines parties de votre design, recto verso. Indiquez les zones de chaque face dans votre fichier.',
  ),
  single_sided_scratch_off_silkscreen: t(
    'Kraslaag aan één zijde: een afdeklaag die je wegkrast om bijvoorbeeld een code of prijs te onthullen. Geef de krasvlakken aan in je bestand.',
    'Scratch-off layer on one side: a coating you scratch away to reveal, for example, a code or prize. Mark the scratch areas in your file.',
    'Rubbelschicht auf einer Seite: eine Deckschicht, die man abrubbelt, um etwa einen Code oder Gewinn freizulegen. Markieren Sie die Rubbelflächen in Ihrer Datei.',
    'Couche à gratter sur une face : un revêtement à gratter pour révéler par exemple un code ou un gain. Indiquez les zones dans votre fichier.',
  ),
};

const LETTERPRESS: Record<string, Localized> = {
  none: t(
    'Geen veredeling aan deze zijde.',
    'No embellishment on this side.',
    'Keine Veredelung auf dieser Seite.',
    'Pas d’ennoblissement sur cette face.',
  ),
  letterpress_no__color: t(
    'Blinddruk: je ontwerp wordt zonder kleur in het papier gedrukt. Je ziet en voelt alleen het reliëf. Ingetogen en luxe.',
    'Blind deboss: your design is pressed into the paper without colour. You only see and feel the relief. Subtle and luxurious.',
    'Blindprägung: Ihr Design wird ohne Farbe ins Papier gedrückt. Sie sehen und fühlen nur das Relief. Dezent und edel.',
    'Gaufrage à sec : votre design est imprimé en creux dans le papier, sans couleur. Seul le relief se voit et se touche. Sobre et luxueux.',
  ),
  letterpress_pms: t(
    'Letterpress in één PMS-kleur: je ontwerp wordt met inkt in het papier gedrukt. Voelbaar, met een ambachtelijke uitstraling.',
    'Letterpress in one PMS colour: your design is pressed into the paper with ink. Tactile, with a crafted look.',
    'Letterpress in einer PMS-Farbe: Ihr Design wird mit Farbe ins Papier gedrückt. Fühlbar, mit handwerklicher Ausstrahlung.',
    'Letterpress en une couleur PMS : votre design est imprimé en creux avec de l’encre. Tactile, avec un rendu artisanal.',
  ),
};

const LETTERPRESS_FOIL = t(
  'Foliedruk: een metallic of glanzende folie wordt in het papier geperst. Opvallend voor logo’s en tekst, met een licht voelbaar reliëf.',
  'Foil print: a metallic or glossy foil is pressed into the paper. Eye-catching for logos and text, with a slightly tactile relief.',
  'Foliendruck: Eine Metallic- oder Glanzfolie wird ins Papier gepresst. Auffällig für Logos und Text, mit leicht fühlbarem Relief.',
  'Marquage à chaud : un film métallisé ou brillant est pressé dans le papier. Idéal pour logos et textes, avec un léger relief.',
);

// ---------------------------------------------------------------------------
// Vaste waarden per optie
// ---------------------------------------------------------------------------

const EXACT: Record<string, Record<string, Localized>> = {
  variable_creasing_line: {
    none: NO_CREASE,
    null: NO_CREASE,
    'creasing_wire-o_desk_calender': t(
      'Drie rillijnen voor de staander van een bureaukalender met wire-o, zodat de voet netjes in vorm vouwt.',
      'Three crease lines for the stand of a wire-o desk calendar, so the base folds neatly into shape.',
      'Drei Rilllinien für den Aufsteller eines Tischkalenders mit Wire-O, damit der Fuß sauber in Form faltet.',
      'Trois rainures pour le support d’un calendrier de bureau à reliure wire-o, afin que le pied se plie proprement.',
    ),
  },
  creasing: {
    yes: CREASED,
    yes_lfp: CREASED,
    no: NOT_CREASED,
    null: NOT_CREASED,
  },
  businesscard_creasing: {
    yes: t(
      'Er komt een ril in het kaartje, zodat het netjes vouwt zonder te barsten. Handig voor gevouwen visitekaartjes.',
      'A crease is added so the card folds neatly without cracking. Useful for folded business cards.',
      'Die Karte erhält eine Rillung, damit sie sauber und ohne Bruch faltet. Praktisch für gefalzte Visitenkarten.',
      'Un rainage est ajouté pour que la carte se plie proprement sans casser. Pratique pour les cartes de visite pliées.',
    ),
    no: t(
      'Geen ril. Kies dit voor gewone vlakke visitekaartjes.',
      'No crease. Choose this for regular flat business cards.',
      'Keine Rillung. Wählen Sie dies für normale flache Visitenkarten.',
      'Pas de rainage. À choisir pour des cartes de visite plates classiques.',
    ),
    null: t(
      'Geen ril. Kies dit voor gewone vlakke visitekaartjes.',
      'No crease. Choose this for regular flat business cards.',
      'Keine Rillung. Wählen Sie dies für normale flache Visitenkarten.',
      'Pas de rainage. À choisir pour des cartes de visite plates classiques.',
    ),
  },
  die_cut: {
    yes: OWN_SHAPE,
    no: NO_CUSTOM_SHAPE,
    none: NO_CUSTOM_SHAPE,
    null: NO_CUSTOM_SHAPE,
    cutting: t(
      'Snij contour: het product wordt digitaal langs jouw snijlijn uitgesneden. Geschikt voor eigen vormen. Lever de contour als vectorlijn aan.',
      'Cut contour: the product is digitally cut along your cut line. Suitable for custom shapes. Supply the contour as a vector line.',
      'Schnittkontur: Das Produkt wird digital entlang Ihrer Schnittlinie geschnitten. Geeignet für eigene Formen. Liefern Sie die Kontur als Vektorlinie.',
      'Contour découpé : le produit est découpé numériquement le long de votre ligne de coupe. Adapté aux formes libres. Fournissez le contour en vectoriel.',
    ),
    cutting_creasing: t(
      'Snij contour met rillijnen: de vorm wordt uitgesneden en de vouwlijnen worden gerild, zodat het product netjes in vorm vouwt. Geef snij- en rillijnen aan in je bestand.',
      'Cut contour with creases: the shape is cut out and the fold lines are creased, so the product folds neatly. Mark cut and crease lines in your file.',
      'Schnittkontur mit Rillungen: Die Form wird ausgeschnitten und die Falzlinien gerillt, damit das Produkt sauber faltet. Markieren Sie Schnitt- und Rilllinien in Ihrer Datei.',
      'Contour découpé avec rainages : la forme est découpée et les plis sont rainés pour un pliage net. Indiquez les lignes de coupe et de rainage dans votre fichier.',
    ),
    punching: t(
      'Stans contour: de vorm wordt met een stansmes uitgestanst. Geeft strakke randen en is vooral geschikt voor grotere oplagen van dezelfde vorm.',
      'Punched contour: the shape is punched out with a cutting die. Gives clean edges and suits larger runs of the same shape.',
      'Stanzkontur: Die Form wird mit einem Stanzwerkzeug ausgestanzt. Ergibt saubere Kanten und eignet sich vor allem für größere Auflagen derselben Form.',
      'Contour à l’emporte-pièce : la forme est découpée avec une forme de découpe. Bords nets, surtout adapté aux grands tirages d’une même forme.',
    ),
    laser: t(
      'Laser contour: de vorm wordt met een laser gesneden. Geschikt voor fijne details en ingewikkelde vormen.',
      'Laser contour: the shape is cut with a laser. Suitable for fine details and intricate shapes.',
      'Laserkontur: Die Form wird mit einem Laser geschnitten. Geeignet für feine Details und komplexe Formen.',
      'Contour laser : la forme est découpée au laser. Adapté aux détails fins et aux formes complexes.',
    ),
    clean_cut: t(
      'Schoonsnijden: recht rondom afgesneden in een rechthoekige vorm, zonder eigen contour.',
      'Trimmed: cut straight on all sides to a rectangular shape, without a custom contour.',
      'Randbeschnitt: rundum gerade auf eine rechteckige Form geschnitten, ohne eigene Kontur.',
      'Coupe franche : coupé droit sur tous les côtés en forme rectangulaire, sans contour personnalisé.',
    ),
  },
  perforation: {
    none: NO_PERFORATION,
    null: NO_PERFORATION,
    yes: t(
      'Met perforatielijn: een rij kleine gaatjes, zodat je een deel netjes afscheurt. Geef de positie aan in je ontwerp.',
      'With perforation: a row of small holes so you can tear off a part neatly. Mark the position in your design.',
      'Mit Perforation: eine Reihe kleiner Löcher, damit Sie einen Teil sauber abtrennen können. Markieren Sie die Position im Design.',
      'Avec perforation : une rangée de petits trous pour détacher proprement une partie. Indiquez la position dans votre design.',
    ),
    '1_perforation_line_block': t(
      'Blokperforatie: een scheurlijn langs de gelijmde of gebonden zijde, zodat je losse vellen netjes uit het blok scheurt.',
      'Block perforation: a tear line along the glued or bound edge, so you can tear single sheets neatly from the pad.',
      'Blockperforation: eine Abreißlinie entlang der geleimten oder gebundenen Seite, damit Sie einzelne Blätter sauber abtrennen.',
      'Perforation de bloc : une ligne détachable le long du bord collé ou relié, pour détacher proprement chaque feuille.',
    ),
  },
  rounded_corners: {
    none: STRAIGHT_CORNERS,
    null: STRAIGHT_CORNERS,
    no_rounded_corners: STRAIGHT_CORNERS,
    rounded_corners: t(
      'Afgeronde hoeken. Geeft een zachtere, moderne uitstraling en de hoeken buigen minder snel om.',
      'Rounded corners. Gives a softer, modern look and the corners are less likely to bend.',
      'Abgerundete Ecken. Wirken weicher und moderner, und die Ecken knicken weniger leicht um.',
      'Coins arrondis. Un aspect plus doux et moderne, et les coins se cornent moins facilement.',
    ),
  },
  drillholes: {
    none: NO_DRILLHOLES,
    null: NO_DRILLHOLES,
    no_drillholes: NO_DRILLHOLES,
    four_holes: t(
      'Vier boorgaten, zodat je het product stevig ophangt of vastschroeft.',
      'Four drill holes, so you can hang or screw the product securely.',
      'Vier Bohrlöcher, damit Sie das Produkt sicher aufhängen oder festschrauben können.',
      'Quatre trous, pour accrocher ou visser le produit solidement.',
    ),
    'one_drillhole_4.8mm_landscape_top_mid': t(
      'Eén boorgat van 4,8 mm in het midden van de bovenzijde.',
      'One 4.8 mm drill hole in the middle of the top edge.',
      'Ein Bohrloch von 4,8 mm mittig an der Oberseite.',
      'Un trou de 4,8 mm au milieu du bord supérieur.',
    ),
    'one_drillhole_4.8mm_landscape_mid_left': t(
      'Eén boorgat van 4,8 mm in het midden van de linkerzijde.',
      'One 4.8 mm drill hole in the middle of the left edge.',
      'Ein Bohrloch von 4,8 mm mittig an der linken Seite.',
      'Un trou de 4,8 mm au milieu du bord gauche.',
    ),
    'one_drillhole_4.8mm_landscape_top_left': t(
      'Eén boorgat van 4,8 mm linksboven.',
      'One 4.8 mm drill hole at the top left.',
      'Ein Bohrloch von 4,8 mm oben links.',
      'Un trou de 4,8 mm en haut à gauche.',
    ),
    one_slot_14_x_3_mm_landscape_top_mid: t(
      'Een sleuf van 14 x 3 mm in het midden van de bovenzijde, bijvoorbeeld voor een clip of keycord.',
      'A 14 x 3 mm slot in the middle of the top edge, for example for a clip or lanyard.',
      'Ein Schlitz von 14 x 3 mm mittig an der Oberseite, zum Beispiel für einen Clip oder ein Umhängeband.',
      'Une fente de 14 x 3 mm au milieu du bord supérieur, par exemple pour une pince ou un tour de cou.',
    ),
    one_slot_14_x_3_mm_landscape_mid_left: t(
      'Een sleuf van 14 x 3 mm in het midden van de linkerzijde, bijvoorbeeld voor een clip of keycord.',
      'A 14 x 3 mm slot in the middle of the left edge, for example for a clip or lanyard.',
      'Ein Schlitz von 14 x 3 mm mittig an der linken Seite, zum Beispiel für einen Clip oder ein Umhängeband.',
      'Une fente de 14 x 3 mm au milieu du bord gauche, par exemple pour une pince ou un tour de cou.',
    ),
    two_drillholes_6mm_tear_off_calendars: t(
      'Twee boorgaten van 6 mm om de scheurkalender op te hangen.',
      'Two 6 mm drill holes to hang the tear-off calendar.',
      'Zwei Bohrlöcher von 6 mm zum Aufhängen des Abreißkalenders.',
      'Deux trous de 6 mm pour accrocher le calendrier éphéméride.',
    ),
    one_drillhole_4mm_eyelet: t(
      'Eén boorgat van 4 mm, verstevigd met een metalen oogje zodat het gat niet uitscheurt.',
      'One 4 mm drill hole reinforced with a metal eyelet, so the hole does not tear.',
      'Ein Bohrloch von 4 mm, verstärkt mit einer Metallöse, damit das Loch nicht ausreißt.',
      'Un trou de 4 mm renforcé par un œillet métallique, pour qu’il ne se déchire pas.',
    ),
    'one_drillhole_fan_deck_5.5mm': t(
      'Eén boorgat van 5,5 mm, waarmee je de kaarten met een schroef of pen samenhoudt als waaier.',
      'One 5.5 mm drill hole, so the cards can be held together with a screw or pin as a fan deck.',
      'Ein Bohrloch von 5,5 mm, mit dem die Karten per Schraube oder Stift als Fächer zusammengehalten werden.',
      'Un trou de 5,5 mm pour assembler les cartes en éventail avec une vis ou une goupille.',
    ),
  },
  lock: {
    none: t('Geen sluiting.', 'No closure.', 'Kein Verschluss.', 'Sans fermeture.'),
    not_applicable: NOT_APPLICABLE,
    velcro: t(
      'Klittenband: eenvoudig te openen en weer te sluiten, ook na vaak gebruik.',
      'Velcro: easy to open and close again, even after frequent use.',
      'Klettverschluss: einfach zu öffnen und wieder zu schließen, auch bei häufiger Nutzung.',
      'Velcro : facile à ouvrir et à refermer, même après un usage fréquent.',
    ),
    gummed: t(
      'Gegomd: de klep heeft een gomlaag die je bevochtigt om te sluiten. De klassieke, voordelige sluiting.',
      'Gummed: the flap has a gum layer you moisten to seal. The classic, economical closure.',
      'Gummiert: Die Klappe hat eine Gummierung, die Sie zum Verschließen befeuchten. Der klassische, günstige Verschluss.',
      'Gommée : le rabat a une couche de colle à humidifier pour fermer. La fermeture classique et économique.',
    ),
    gummed_flap_long_side: t(
      'Gegomd, met de klep aan de lange zijde: je bevochtigt de gomlaag om te sluiten.',
      'Gummed, with the flap on the long side: moisten the gum layer to seal.',
      'Gummiert, mit der Klappe an der langen Seite: Befeuchten Sie die Gummierung zum Verschließen.',
      'Gommée, avec le rabat sur le grand côté : humidifiez la colle pour fermer.',
    ),
    striplock: t(
      'Striplock: een zelfklevende sluiting met een afdekstrip. Strip eraf trekken en dichtdrukken, zonder bevochtigen.',
      'Striplock: a self-adhesive closure with a cover strip. Peel off the strip and press shut, no moistening needed.',
      'Haftklebeverschluss: selbstklebend mit Abdeckstreifen. Streifen abziehen und zudrücken, ohne Befeuchten.',
      'Fermeture autocollante à bande : retirez la bande protectrice et appuyez pour fermer, sans humidifier.',
    ),
    striplock_with_tear_strip: t(
      'Striplock met afscheurstrook: zelfklevend sluiten zonder bevochtigen, en de ontvanger opent hem netjes via de scheurstrook.',
      'Striplock with tear strip: self-adhesive closing without moistening, and the recipient opens it neatly with the tear strip.',
      'Haftklebeverschluss mit Aufreißfaden: selbstklebend ohne Befeuchten, und der Empfänger öffnet ihn sauber über den Aufreißstreifen.',
      'Fermeture autocollante avec bande d’arrachage : se ferme sans humidifier et s’ouvre proprement grâce à la bande.',
    ),
    dubble_striplock_with_tear_strip: t(
      'Twee zelfklevende sluitstrips met afscheurstrook. Na openen kun je de verpakking nog een keer sluiten, bijvoorbeeld voor een retourzending.',
      'Two self-adhesive closing strips with a tear strip. After opening, the packaging can be sealed once more, for example for a return.',
      'Zwei selbstklebende Verschlussstreifen mit Aufreißstreifen. Nach dem Öffnen lässt sich die Verpackung erneut verschließen, etwa für eine Rücksendung.',
      'Deux bandes autocollantes avec bande d’arrachage. Après ouverture, l’emballage peut être refermé une fois, par exemple pour un retour.',
    ),
    adhesive_strip: t(
      'Plakstrook: een zelfklevende rand waarmee je de verpakking snel dichtplakt.',
      'Adhesive strip: a self-adhesive edge to seal the packaging quickly.',
      'Klebestreifen: ein selbstklebender Rand, mit dem Sie die Verpackung schnell verschließen.',
      'Bande adhésive : un bord autocollant pour fermer l’emballage rapidement.',
    ),
    adhesive_strip_with_backing: t(
      'Plakstrook met afneembare beschermstrip: trek de strip eraf en druk dicht. Geen bevochtigen nodig.',
      'Adhesive strip with removable liner: peel off the liner and press shut. No moistening needed.',
      'Klebestreifen mit abziehbarer Schutzfolie: Folie abziehen und zudrücken. Kein Befeuchten nötig.',
      'Bande adhésive avec film détachable : retirez le film et appuyez pour fermer. Pas besoin d’humidifier.',
    ),
    resealable_packaging: t(
      'Hersluitbaar: de verpakking kan na openen weer gesloten worden.',
      'Resealable: the packaging can be closed again after opening.',
      'Wiederverschließbar: Die Verpackung lässt sich nach dem Öffnen wieder schließen.',
      'Refermable : l’emballage peut être refermé après ouverture.',
    ),
    pin: t(
      'Speld aan de achterkant, om het product op kleding of een tas te bevestigen.',
      'A pin on the back to attach the product to clothing or a bag.',
      'Eine Nadel auf der Rückseite, um das Produkt an Kleidung oder einer Tasche zu befestigen.',
      'Une épingle au dos pour fixer le produit sur un vêtement ou un sac.',
    ),
    magnet: t(
      'Magneet: bevestigt met een magneet, zonder speld en zonder gaatjes in kleding.',
      'Magnet: attaches with a magnet, without a pin and without holes in clothing.',
      'Magnet: Befestigung per Magnet, ohne Nadel und ohne Löcher in der Kleidung.',
      'Aimant : se fixe avec un aimant, sans épingle et sans trous dans les vêtements.',
    ),
    longitudinal_seam_glued_1: t(
      'De lange naad van de verpakking wordt één keer gelijmd. Je vouwt de verpakking daarna eenvoudig op.',
      'The long seam of the packaging is glued once. You then simply fold the packaging into shape.',
      'Die Längsnaht der Verpackung wird einmal verklebt. Danach falten Sie die Verpackung einfach auf.',
      'La couture longitudinale de l’emballage est collée une fois. Il suffit ensuite de le mettre en forme.',
    ),
  },
  spot_finish: SPOT_FINISH,
  spot_finish_back: SPOT_FINISH,
  hotfoil: {
    none: t(
      'Geen foliedruk. Je ontwerp wordt alleen gedrukt.',
      'No foil print. Your design is printed only.',
      'Kein Foliendruck. Ihr Design wird nur gedruckt.',
      'Pas de marquage à chaud. Votre design est uniquement imprimé.',
    ),
  },
  foil_print_first_color: LETTERPRESS,
  foil_print_second_color: LETTERPRESS,
  binding: {
    calendar_hanger: t(
      'Wire-o met kalenderhaak: een metalen spiraalbinding met ophanghaakje, zodat je de kalender direct aan de muur hangt.',
      'Wire-o with calendar hanger: a metal wire binding with a hook, so you can hang the calendar straight on the wall.',
      'Wire-O mit Kalenderaufhänger: eine Metall-Drahtbindung mit Haken, damit Sie den Kalender direkt an die Wand hängen.',
      'Wire-o avec crochet : une reliure métallique avec crochet, pour accrocher le calendrier directement au mur.',
    ),
    stapled: t(
      'Geniet gebrocheerd: de pagina’s worden op de rug met nietjes vastgezet. Voordelig en geschikt voor dunnere brochures.',
      'Saddle stitched: the pages are fixed with staples on the spine. Economical and suitable for thinner brochures.',
      'Rückendrahtheftung: Die Seiten werden am Rücken mit Klammern geheftet. Günstig und geeignet für dünnere Broschüren.',
      'Piqûre à cheval : les pages sont agrafées au dos. Économique et adapté aux brochures fines.',
    ),
    loop_stitched: t(
      'Oognieten: nietjes met een oogje op de rug, zodat je het drukwerk in een ringband kunt bewaren.',
      'Loop stitched: staples with a loop on the spine, so you can keep the print in a ring binder.',
      'Ösenheftung: Klammern mit Öse am Rücken, damit Sie das Druckwerk in einem Ringordner abheften können.',
      'Piqûre à œillets : agrafes à boucle au dos, pour ranger l’imprimé dans un classeur à anneaux.',
    ),
    glued: t(
      'Gelijmd: de vellen worden aan één zijde aan elkaar gelijmd.',
      'Glued: the sheets are glued together along one edge.',
      'Geleimt: Die Blätter werden an einer Seite zusammengeleimt.',
      'Collé : les feuilles sont collées ensemble sur un côté.',
    ),
    blocknotes_bounded: t(
      'Gelijmd blok: de vellen zijn aan één zijde gelijmd, zodat je ze één voor één afscheurt.',
      'Glued pad: the sheets are glued along one edge, so you can tear them off one by one.',
      'Geleimter Block: Die Blätter sind an einer Seite geleimt, sodass Sie sie einzeln abreißen können.',
      'Bloc collé : les feuilles sont collées sur un côté pour être détachées une par une.',
    ),
    top_side_glued: t(
      'Plakrand aan de bovenzijde: de vellen zijn bovenaan gelijmd, zodat je ze één voor één afscheurt.',
      'Glued at the top: the sheets are glued along the top edge, so you can tear them off one by one.',
      'Leimkante oben: Die Blätter sind oben geleimt, sodass Sie sie einzeln abreißen können.',
      'Collé en haut : les feuilles sont collées sur le bord supérieur pour être détachées une par une.',
    ),
    left_side_glued: t(
      'Plakrand aan de linkerzijde: de vellen zijn links gelijmd, zodat je ze één voor één afscheurt.',
      'Glued on the left: the sheets are glued along the left edge, so you can tear them off one by one.',
      'Leimkante links: Die Blätter sind links geleimt, sodass Sie sie einzeln abreißen können.',
      'Collé à gauche : les feuilles sont collées sur le bord gauche pour être détachées une par une.',
    ),
    glued_pillow_pack: t(
      'De verpakking wordt aan de lange zijde dichtgelijmd.',
      'The packaging is glued shut along the long side.',
      'Die Verpackung wird an der langen Seite verklebt.',
      'L’emballage est collé sur le grand côté.',
    ),
    bounded: t(
      'Gelijmd gebrocheerd: de pagina’s worden in een omslag met een vierkante rug gelijmd. Netjes voor dikkere brochures, catalogi en boeken.',
      'Perfect bound: the pages are glued into a cover with a square spine. Neat for thicker brochures, catalogues and books.',
      'Klebebindung: Die Seiten werden in einen Umschlag mit eckigem Rücken geleimt. Sauber für dickere Broschüren, Kataloge und Bücher.',
      'Dos carré collé : les pages sont collées dans une couverture à dos carré. Idéal pour brochures épaisses, catalogues et livres.',
    ),
    stitched: t(
      'Genaaid gebrocheerd: de katernen worden met garen genaaid en in de omslag gelijmd. Zeer sterk, en het boek opent vlakker.',
      'Sewn binding: the sections are sewn with thread and glued into the cover. Very strong, and the book opens flatter.',
      'Fadenheftung: Die Lagen werden mit Faden geheftet und in den Umschlag geleimt. Sehr stabil, und das Buch öffnet flacher.',
      'Dos carré cousu : les cahiers sont cousus au fil puis collés dans la couverture. Très solide, et le livre s’ouvre plus à plat.',
    ),
    wireo: t(
      'Wire-o: een metalen dubbele spiraalbinding. Het drukwerk ligt plat open en je slaat pagina’s helemaal om.',
      'Wire-o: a metal double-loop binding. The print lies flat when open and pages fold all the way back.',
      'Wire-O: eine Metall-Doppeldrahtbindung. Das Druckwerk liegt flach auf und Seiten lassen sich ganz umschlagen.',
      'Wire-o : une reliure métallique à double boucle. Le document reste à plat et les pages se replient entièrement.',
    ),
    stapled_pad: t(
      'Geniet en geperforeerd: het blok wordt geniet en elk vel heeft een perforatie, zodat je het netjes afscheurt.',
      'Stapled and perforated: the pad is stapled and each sheet is perforated, so you can tear it off neatly.',
      'Geheftet und perforiert: Der Block wird geheftet und jedes Blatt perforiert, damit Sie es sauber abtrennen.',
      'Agrafé et perforé : le bloc est agrafé et chaque feuille est perforée pour être détachée proprement.',
    ),
    newspaper_perfect_bound: t(
      'Los ingestoken: de katernen worden in elkaar gestoken zonder nietjes, zoals bij een krant.',
      'Loosely inserted: the sections are nested inside each other without staples, like a newspaper.',
      'Lose ineinandergesteckt: Die Lagen werden ohne Heftung ineinandergelegt, wie bei einer Zeitung.',
      'Encarté sans agrafes : les cahiers sont insérés les uns dans les autres, comme un journal.',
    ),
    newspaper_stapled: t(
      'Ingestoken en geniet: de katernen worden in elkaar gestoken en op de rug geniet, zodat de pagina’s bij elkaar blijven.',
      'Inserted and stapled: the sections are nested and stapled on the spine, so the pages stay together.',
      'Ineinandergesteckt und geheftet: Die Lagen werden ineinandergelegt und am Rücken geheftet, damit die Seiten zusammenbleiben.',
      'Encarté et agrafé : les cahiers sont insérés puis agrafés au dos pour que les pages restent ensemble.',
    ),
    top_glued: t(
      'Bovenaan gelijmd, inclusief dagaanwijzer om de huidige dag aan te geven.',
      'Glued at the top, including a day marker to show the current day.',
      'Oben geleimt, inklusive Tagesanzeiger zum Markieren des aktuellen Tages.',
      'Collé en haut, avec un indicateur de jour pour marquer la date du jour.',
    ),
    wireo_desk_month_calendar: t(
      'Wire-o aan de bovenzijde, inclusief dagaanwijzer om de huidige dag aan te geven. Je slaat de maanden eenvoudig om.',
      'Wire-o at the top, including a day marker to show the current day. The months flip over easily.',
      'Wire-O oben, inklusive Tagesanzeiger zum Markieren des aktuellen Tages. Die Monate lassen sich einfach umblättern.',
      'Wire-o en haut, avec un indicateur de jour pour marquer la date du jour. Les mois se tournent facilement.',
    ),
    seperate_pages: t(
      'Losse pagina’s, zonder binding. Handig als je zelf wilt bundelen of inbinden.',
      'Loose pages without binding. Handy if you want to bundle or bind them yourself.',
      'Lose Seiten ohne Bindung. Praktisch, wenn Sie selbst bündeln oder binden möchten.',
      'Pages libres, sans reliure. Pratique si vous voulez les assembler ou les relier vous-même.',
    ),
  },
  binding_edge: {
    binding_top: t(
      'Gebonden aan de bovenzijde: je slaat de pagina’s naar boven om, zoals bij een wandkalender of schrijfblok.',
      'Bound at the top: pages flip upwards, like a wall calendar or writing pad.',
      'Bindung oben: Die Seiten werden nach oben umgeschlagen, wie bei einem Wandkalender oder Schreibblock.',
      'Reliure en haut : les pages se tournent vers le haut, comme un calendrier mural ou un bloc-notes.',
    ),
    binding_left: t(
      'Gebonden aan de linkerzijde: je bladert zoals in een boek.',
      'Bound on the left: you leaf through it like a book.',
      'Bindung links: Sie blättern wie in einem Buch.',
      'Reliure à gauche : on feuillette comme un livre.',
    ),
    binding_bottom: t(
      'Gebonden aan de onderzijde. Kies dit als je ontwerp hierop is gemaakt.',
      'Bound at the bottom. Choose this if your design is made for it.',
      'Bindung unten. Wählen Sie dies, wenn Ihr Design dafür angelegt ist.',
      'Reliure en bas. À choisir si votre design est conçu pour cela.',
    ),
    glued_backside: t(
      'Plakrand aan de achterzijde: de vellen zijn aan de achterkant gelijmd.',
      'Glued at the back: the sheets are glued along the back.',
      'Leimkante auf der Rückseite: Die Blätter sind hinten geleimt.',
      'Collé au dos : les feuilles sont collées à l’arrière.',
    ),
  },
  white_print: {
    none: t(
      'Geen witte inkt. Op transparant of gekleurd materiaal schijnt het materiaal dan door de kleuren heen.',
      'No white ink. On transparent or coloured material, the material will show through the colours.',
      'Keine weiße Farbe. Auf transparentem oder farbigem Material scheint das Material durch die Farben.',
      'Sans encre blanche. Sur un support transparent ou coloré, le support transparaît à travers les couleurs.',
    ),
    full_white: t(
      'Volledig opvullen: onder je hele ontwerp komt een laag wit, zodat kleuren op transparant of gekleurd materiaal dekkend en helder zijn.',
      'Full white: a white layer under your entire design, so colours are opaque and bright on transparent or coloured material.',
      'Vollflächig Weiß: eine weiße Schicht unter dem gesamten Design, damit Farben auf transparentem oder farbigem Material deckend und klar sind.',
      'Blanc intégral : une couche de blanc sous tout votre design, pour des couleurs opaques et vives sur support transparent ou coloré.',
    ),
    selective_white: t(
      'Wit alleen waar jij het aangeeft met een steunkleur in je bestand. Handig als delen transparant moeten blijven.',
      'White only where you mark it with a spot colour in your file. Useful when parts should stay transparent.',
      'Weiß nur dort, wo Sie es mit einer Sonderfarbe in Ihrer Datei markieren. Praktisch, wenn Teile transparent bleiben sollen.',
      'Blanc uniquement là où vous l’indiquez avec une couleur d’accompagnement. Utile si certaines zones doivent rester transparentes.',
    ),
  },
  underbase: {
    yes: t(
      'Dekwit: eerst een witte laag onder de kleuren, zodat ze op transparant of donker materiaal helder en dekkend zijn.',
      'White underbase: a white layer is printed under the colours first, so they stay bright and opaque on transparent or dark material.',
      'Deckweiß: zuerst eine weiße Schicht unter den Farben, damit sie auf transparentem oder dunklem Material leuchtend und deckend sind.',
      'Sous-couche blanche : une couche de blanc sous les couleurs pour qu’elles restent vives et opaques sur un support transparent ou foncé.',
    ),
    no: t(
      'Zonder dekwit. Kleuren worden direct op het materiaal gedrukt en kunnen op transparant of donker materiaal doorschijnend lijken.',
      'No white underbase. Colours are printed directly on the material and may look translucent on transparent or dark material.',
      'Ohne Deckweiß. Farben werden direkt auf das Material gedruckt und können auf transparentem oder dunklem Material durchscheinend wirken.',
      'Sans sous-couche blanche. Les couleurs sont imprimées directement et peuvent paraître translucides sur un support transparent ou foncé.',
    ),
  },
  fold: {
    single_4: t(
      'Enkele vouw aan de linkerzijde: één vouw geeft 4 pagina’s, en de folder opent als een boekje.',
      'Single fold on the left: one fold gives 4 pages, and the leaflet opens like a booklet.',
      'Einbruchfalz links: Ein Falz ergibt 4 Seiten, und der Flyer öffnet sich wie ein Heft.',
      'Pli simple à gauche : un pli donne 4 pages, et le dépliant s’ouvre comme un livret.',
    ),
    singlehorizontal_4: t(
      'Enkele vouw aan de bovenzijde: één vouw geeft 4 pagina’s, en de folder opent naar boven.',
      'Single fold at the top: one fold gives 4 pages, and the leaflet opens upwards.',
      'Einbruchfalz oben: Ein Falz ergibt 4 Seiten, und der Flyer öffnet sich nach oben.',
      'Pli simple en haut : un pli donne 4 pages, et le dépliant s’ouvre vers le haut.',
    ),
    luik_6: t(
      'Luikvouw met 6 pagina’s: beide zijpanelen vouwen naar het midden, zoals twee deurtjes die openen.',
      'Gate fold with 6 pages: both side panels fold towards the centre, like two doors opening.',
      'Fensterfalz mit 6 Seiten: Beide Seitenteile falten zur Mitte, wie zwei Türen, die sich öffnen.',
      'Pli fenêtre de 6 pages : les deux volets latéraux se replient vers le centre, comme deux portes.',
    ),
    luik_8: t(
      'Gesloten luikvouw met 8 pagina’s: de zijpanelen vouwen naar het midden en daarna wordt alles nog een keer dubbelgevouwen.',
      'Closed gate fold with 8 pages: the side panels fold to the centre and the whole sheet is then folded in half once more.',
      'Altarfalz geschlossen mit 8 Seiten: Die Seitenteile falten zur Mitte, danach wird alles noch einmal gefaltet.',
      'Pli fenêtre fermé de 8 pages : les volets se replient vers le centre, puis l’ensemble est plié une fois de plus en deux.',
    ),
    cross_8: t(
      'Kruisvouw met 8 pagina’s: eerst één vouw, daarna een tweede vouw haaks daarop.',
      'Cross fold with 8 pages: one fold, then a second fold at a right angle to it.',
      'Kreuzbruchfalz mit 8 Seiten: zuerst ein Falz, danach ein zweiter Falz im rechten Winkel dazu.',
      'Pli croisé de 8 pages : un premier pli, puis un second pli perpendiculaire.',
    ),
    no_fold: t(
      'Ongevouwen: je ontvangt het product plat.',
      'Unfolded: you receive the product flat.',
      'Ungefalzt: Sie erhalten das Produkt flach.',
      'Non plié : vous recevez le produit à plat.',
    ),
    not_fold: t(
      'Ongevouwen: je ontvangt het product plat.',
      'Unfolded: you receive the product flat.',
      'Ungefalzt: Sie erhalten das Produkt flach.',
      'Non plié : vous recevez le produit à plat.',
    ),
    '1/4_fold': t(
      'Kwartvouw: twee keer gevouwen tot een kwart van het open formaat.',
      'Quarter fold: folded twice to a quarter of the open size.',
      'Viertelfalz: zweimal gefaltet auf ein Viertel des offenen Formats.',
      'Pli en quatre : plié deux fois au quart du format ouvert.',
    ),
    '1/8_fold': t(
      'Achtstevouw: drie keer gevouwen tot een achtste van het open formaat.',
      'Eighth fold: folded three times to an eighth of the open size.',
      'Achtelfalz: dreimal gefaltet auf ein Achtel des offenen Formats.',
      'Pli en huit : plié trois fois au huitième du format ouvert.',
    ),
    cutlery_fold: t(
      'Bestekvouw: zo gevouwen dat er een vakje ontstaat waar je bestek in steekt.',
      'Cutlery fold: folded to form a pocket you can slide cutlery into.',
      'Bestecktaschenfalz: so gefaltet, dass eine Tasche für Besteck entsteht.',
      'Pli pochette à couverts : plié pour former une poche où glisser les couverts.',
    ),
  },
  folded: {
    no: t(
      'Plano geleverd: ongevouwen, handig als je zelf wilt vouwen of nabewerken.',
      'Delivered flat: unfolded, handy if you want to fold or finish it yourself.',
      'Plano geliefert: ungefalzt, praktisch, wenn Sie selbst falzen oder weiterverarbeiten möchten.',
      'Livré à plat : non plié, pratique si vous voulez plier ou façonner vous-même.',
    ),
    yes: t(
      'Gevouwen geleverd volgens de gekozen vouwwijze, direct klaar voor gebruik.',
      'Delivered folded according to the chosen fold, ready to use.',
      'Gefalzt geliefert nach der gewählten Falzart, direkt einsatzbereit.',
      'Livré plié selon le pli choisi, prêt à l’emploi.',
    ),
  },
  score: {
    none: t(
      'Niet insnijden. De plaat blijft heel en vlak.',
      'No scoring. The board stays whole and flat.',
      'Kein Anritzen. Die Platte bleibt ganz und flach.',
      'Pas d’incision. Le panneau reste entier et plat.',
    ),
    front: t(
      'Insnijden aan de voorkant: de plaat wordt aan de voorzijde gedeeltelijk ingesneden, zodat je hem langs die lijn netjes vouwt. Kies de kant op basis van je vouwrichting.',
      'Scored on the front: the board is partly cut on the front so you can fold it neatly along that line. Choose the side based on your fold direction.',
      'Anritzen vorne: Die Platte wird vorne teilweise eingeschnitten, damit Sie sie entlang dieser Linie sauber falten. Wählen Sie die Seite je nach Faltrichtung.',
      'Incision au recto : le panneau est partiellement entaillé au recto pour le plier proprement le long de cette ligne. Choisissez le côté selon le sens du pli.',
    ),
    back: t(
      'Insnijden aan de achterkant: de plaat wordt aan de achterzijde gedeeltelijk ingesneden, zodat je hem langs die lijn netjes vouwt. Kies de kant op basis van je vouwrichting.',
      'Scored on the back: the board is partly cut on the back so you can fold it neatly along that line. Choose the side based on your fold direction.',
      'Anritzen hinten: Die Platte wird hinten teilweise eingeschnitten, damit Sie sie entlang dieser Linie sauber falten. Wählen Sie die Seite je nach Faltrichtung.',
      'Incision au verso : le panneau est partiellement entaillé au verso pour le plier proprement le long de cette ligne. Choisissez le côté selon le sens du pli.',
    ),
  },
  vcut: {
    none: t(
      'Geen V-cut. De plaat blijft heel en vlak.',
      'No V-cut. The board stays whole and flat.',
      'Kein V-Schnitt. Die Platte bleibt ganz und flach.',
      'Pas de rainure en V. Le panneau reste entier et plat.',
    ),
    front: t(
      'V-cut aan de voorkant: een V-vormige groef in de plaat, zodat je hem strak in een hoek vouwt, bijvoorbeeld voor een display.',
      'V-cut on the front: a V-shaped groove in the board, so you can fold it into a sharp corner, for example for a display.',
      'V-Schnitt vorne: eine V-förmige Nut in der Platte, damit Sie sie zu einer exakten Ecke falten, etwa für ein Display.',
      'Rainure en V au recto : une rainure en V dans le panneau pour le plier en angle net, par exemple pour un présentoir.',
    ),
    back: t(
      'V-cut aan de achterkant: een V-vormige groef in de plaat, zodat je hem strak in een hoek vouwt, bijvoorbeeld voor een display.',
      'V-cut on the back: a V-shaped groove in the board, so you can fold it into a sharp corner, for example for a display.',
      'V-Schnitt hinten: eine V-förmige Nut in der Platte, damit Sie sie zu einer exakten Ecke falten, etwa für ein Display.',
      'Rainure en V au verso : une rainure en V dans le panneau pour le plier en angle net, par exemple pour un présentoir.',
    ),
    'v-cut_5_mm_45': t(
      'V-cut van 5 mm onder 45°: een V-vormige groef, zodat je de plaat in een strakke hoek vouwt.',
      'V-cut of 5 mm at 45°: a V-shaped groove so you can fold the board into a sharp corner.',
      'V-Schnitt von 5 mm in 45°: eine V-förmige Nut, damit Sie die Platte zu einer exakten Ecke falten.',
      'Rainure en V de 5 mm à 45° : pour plier le panneau en un angle net.',
    ),
  },
  clean_cut: {
    yes: CLEAN_CUT_YES,
    no: CLEAN_CUT_NO,
    null: CLEAN_CUT_NO,
  },
  cutting: {
    cut: t(
      'Recht gesneden: rondom recht afgesneden op het gekozen formaat.',
      'Straight cut: cut straight on all sides to the chosen size.',
      'Gerade geschnitten: rundum gerade auf das gewählte Format geschnitten.',
      'Coupe droite : coupé droit sur tous les côtés au format choisi.',
    ),
    contour: t(
      'Contourgesneden: uitgesneden langs de vorm van je ontwerp. Lever een duidelijke snijlijn aan in je bestand.',
      'Contour cut: cut along the shape of your design. Supply a clear cut line in your file.',
      'Konturgeschnitten: entlang der Form Ihres Designs geschnitten. Liefern Sie eine klare Schnittlinie in Ihrer Datei.',
      'Découpe à la forme : découpé selon la forme de votre design. Fournissez une ligne de découpe claire dans votre fichier.',
    ),
  },
  custom_shape: {
    clean_cut: t(
      'Recht rondom afgesneden. Beste keuze voor rechthoekige en vierkante vormen.',
      'Straight cut on all sides. Best for rectangular and square shapes.',
      'Rundum gerade geschnitten. Beste Wahl für rechteckige und quadratische Formen.',
      'Coupe droite sur tous les côtés. Idéal pour les formes rectangulaires et carrées.',
    ),
    clean_cut_stickers: t(
      'Recht rondom afgesneden. Kies dit voor rechthoekige of vierkante stickers zonder contourvorm.',
      'Straight cut on all sides. Choose this for rectangular or square stickers without a contour shape.',
      'Rundum gerade geschnitten. Wählen Sie dies für rechteckige oder quadratische Aufkleber ohne Konturform.',
      'Coupe droite sur tous les côtés. À choisir pour des stickers rectangulaires ou carrés sans contour.',
    ),
    clean_cut_banners: t(
      'Recht rondom afgesneden op het gekozen formaat, zonder eigen contour.',
      'Cut straight on all sides to the chosen size, without a custom contour.',
      'Rundum gerade auf das gewählte Format geschnitten, ohne eigene Kontur.',
      'Coupe droite sur tous les côtés au format choisi, sans contour personnalisé.',
    ),
    clean_cut_flags: t(
      'Standaard afwerking voor vlaggen, recht afgesneden op het gekozen formaat.',
      'Standard finish for flags, cut straight to the chosen size.',
      'Standardverarbeitung für Fahnen, gerade auf das gewählte Format geschnitten.',
      'Finition standard pour drapeaux, coupée droite au format choisi.',
    ),
    no_clean_cut: t(
      'Ruimer gesneden in plaats van precies op eindformaat, dus er kan een smalle rand rond je ontwerp blijven.',
      'Cut with extra margin instead of exactly to final size, so a narrow border may remain around your design.',
      'Mit Zugabe statt exakt auf Endformat geschnitten, daher kann ein schmaler Rand um Ihr Design bleiben.',
      'Coupé avec une marge plutôt qu’au format exact, une fine bordure peut donc rester autour de votre design.',
    ),
    custom_shape_simple: t(
      'Contoursnijden voor één eenvoudige vorm. Lever een duidelijke snijlijn aan in je bestand.',
      'Contour cut for one simple shape. Supply a clear cut line in your file.',
      'Konturschnitt für eine einfache Form. Liefern Sie eine klare Schnittlinie in Ihrer Datei.',
      'Découpe à la forme pour une forme simple. Fournissez une ligne de découpe claire dans votre fichier.',
    ),
    custom_shape_complex: t(
      'Contoursnijden voor één complexere vorm. Kies dit alleen als je bestand een goede vectorsnijlijn bevat.',
      'Contour cut for one more complex shape. Only choose this if your file contains a good vector cut line.',
      'Konturschnitt für eine komplexere Form. Nur wählen, wenn Ihre Datei eine gute Vektor-Schnittlinie enthält.',
      'Découpe à la forme pour une forme plus complexe. À choisir uniquement si votre fichier contient un bon tracé vectoriel.',
    ),
    custom_shape_multiple: t(
      'Contoursnijden voor maximaal 5 losse vormen. Zet elke vorm met een eigen snijlijn in je bestand.',
      'Contour cut for up to 5 separate shapes. Give each shape its own cut line in your file.',
      'Konturschnitt für bis zu 5 einzelne Formen. Geben Sie jeder Form eine eigene Schnittlinie in Ihrer Datei.',
      'Découpe à la forme pour 5 formes séparées maximum. Donnez à chaque forme sa propre ligne de découpe.',
    ),
    custom_shape_multiple_letters: t(
      'Contoursnijden in meerdere vormen, bijvoorbeeld losse letters. Zet elke vorm met een eigen snijlijn in je bestand.',
      'Contour cut into several shapes, for example separate letters. Give each shape its own cut line in your file.',
      'Konturschnitt in mehrere Formen, zum Beispiel einzelne Buchstaben. Geben Sie jeder Form eine eigene Schnittlinie.',
      'Découpe en plusieurs formes, par exemple des lettres séparées. Donnez à chaque forme sa propre ligne de découpe.',
    ),
    custom_shape_banners: t(
      'Contoursnijden: uitgesneden langs de vorm van je ontwerp. Lever een duidelijke snijlijn aan in je bestand.',
      'Contour cut: cut along the shape of your design. Supply a clear cut line in your file.',
      'Konturschnitt: entlang der Form Ihres Designs geschnitten. Liefern Sie eine klare Schnittlinie in Ihrer Datei.',
      'Découpe à la forme : découpé selon la forme de votre design. Fournissez une ligne de découpe claire.',
    ),
    die_cut: t(
      'Contour snijden: uitgesneden langs de vorm van je ontwerp. Lever een duidelijke snijlijn aan in je bestand.',
      'Contour cut: cut along the shape of your design. Supply a clear cut line in your file.',
      'Konturschnitt: entlang der Form Ihres Designs geschnitten. Liefern Sie eine klare Schnittlinie in Ihrer Datei.',
      'Découpe à la forme : découpé selon la forme de votre design. Fournissez une ligne de découpe claire.',
    ),
    custom_shape_packaging: t(
      'Contoursnijden met rillijnen: de vorm wordt uitgesneden en gerild, zodat de verpakking netjes in vorm vouwt.',
      'Contour cut with creases: the shape is cut and creased, so the packaging folds neatly into shape.',
      'Konturschnitt mit Rillungen: Die Form wird geschnitten und gerillt, damit die Verpackung sauber in Form faltet.',
      'Découpe à la forme avec rainages : la forme est découpée et rainée pour que l’emballage se plie proprement.',
    ),
    custom_shape_die_cut: t(
      'Thrucut: de sticker wordt helemaal door stickerlaag en drager heen in vorm gesneden. Je ontvangt losse stickers in de vorm van je ontwerp.',
      'Through cut: the sticker is cut to shape through both the sticker layer and the backing. You receive separate contour stickers.',
      'Durchschnitt: Aufkleber und Trägermaterial werden komplett in Form geschnitten. Sie erhalten einzelne Konturaufkleber.',
      'Découpe complète : le sticker et son support sont découpés à la forme. Vous recevez des stickers individuels.',
    ),
    custom_shape_kiss_cut: t(
      'Kisscut: alleen de stickerlaag wordt gesneden. De drager blijft heel, waardoor de sticker makkelijk los te halen is.',
      'Kiss cut: only the sticker layer is cut. The backing stays intact, so the sticker is easy to peel off.',
      'Anstanzung: Nur die Aufkleberschicht wird geschnitten. Der Träger bleibt ganz, sodass sich der Aufkleber leicht abziehen lässt.',
      'Découpe mi-chair : seule la couche du sticker est découpée. Le support reste intact pour un décollage facile.',
    ),
    custom_shape_kiss_cut_multiple: t(
      'Kisscut: alleen de stickerlaag wordt gesneden, de drager blijft heel. Geschikt voor één of meerdere vormen op een vel.',
      'Kiss cut: only the sticker layer is cut and the backing stays intact. Suitable for one or more shapes on a sheet.',
      'Anstanzung: Nur die Aufkleberschicht wird geschnitten, der Träger bleibt ganz. Für eine oder mehrere Formen auf einem Bogen.',
      'Découpe mi-chair : seule la couche du sticker est découpée, le support reste intact. Pour une ou plusieurs formes par planche.',
    ),
    custom_shape_application_foil: t(
      'Kisscut met applicatiefolie. Handig voor teksten, logo’s en losse vormen die bij het plakken netjes op hun plek moeten blijven.',
      'Kiss cut with application tape. Useful for text, logos and separate shapes that must stay aligned while applying.',
      'Anstanzung mit Übertragungsfolie. Praktisch für Texte, Logos und einzelne Formen, die beim Anbringen exakt positioniert bleiben sollen.',
      'Découpe mi-chair avec film de transfert. Pratique pour textes, logos et formes séparées qui doivent rester alignés à la pose.',
    ),
    custom_shape_doming_stickers: t(
      'Doming sticker in een eigen vorm, met één contourlijn rond je ontwerp.',
      'Domed sticker in a custom shape, with one contour line around your design.',
      'Doming-Aufkleber in eigener Form, mit einer Konturlinie um Ihr Design.',
      'Sticker bombé à la forme de votre choix, avec une ligne de contour autour du design.',
    ),
    rectangular: t(
      'Rechthoekig met licht afgeronde hoeken (1,5 mm radius).',
      'Rectangular with slightly rounded corners (1.5 mm radius).',
      'Rechteckig mit leicht abgerundeten Ecken (1,5 mm Radius).',
      'Rectangulaire avec coins légèrement arrondis (rayon de 1,5 mm).',
    ),
    oval: t('Ovale vorm.', 'Oval shape.', 'Ovale Form.', 'Forme ovale.'),
    circle: t('Ronde vorm.', 'Round shape.', 'Runde Form.', 'Forme ronde.'),
    sheet_whole_vouchers: t(
      'Geleverd als vel met hele bonnen.',
      'Supplied as a sheet of whole vouchers.',
      'Geliefert als Bogen mit ganzen Bons.',
      'Livré en planche de bons entiers.',
    ),
    sheet_whole_and_half_a_vouchers: t(
      'Geleverd als vel met zowel hele als halve bonnen.',
      'Supplied as a sheet with both whole and half vouchers.',
      'Geliefert als Bogen mit ganzen und halben Bons.',
      'Livré en planche avec des bons entiers et des demi-bons.',
    ),
    strip_whole_vouchers: t(
      'Geleverd als strip met hele bonnen.',
      'Supplied as a strip of whole vouchers.',
      'Geliefert als Streifen mit ganzen Bons.',
      'Livré en bande de bons entiers.',
    ),
    strip_whole_and_half_a_vouchers: t(
      'Geleverd als strip met zowel hele als halve bonnen.',
      'Supplied as a strip with both whole and half vouchers.',
      'Geliefert als Streifen mit ganzen und halben Bons.',
      'Livré en bande avec des bons entiers et des demi-bons.',
    ),
    scooter_display: t(
      'Uitgesneden in de vaste vorm van een scooterdakje.',
      'Cut to the fixed shape of a scooter roof sign.',
      'In die feste Form eines Rollerdach-Schilds geschnitten.',
      'Découpé à la forme fixe d’un toit de scooter.',
    ),
    bike_display: t(
      'Uitgesneden in de vaste vorm van een fietsdakje.',
      'Cut to the fixed shape of a bicycle roof sign.',
      'In die feste Form eines Fahrraddach-Schilds geschnitten.',
      'Découpé à la forme fixe d’un toit de vélo.',
    ),
  },
  number_of_contour_lines: {},
  finish_all_around: EDGE_FINISH,
  finish_top: EDGE_FINISH,
  finish_bottom: EDGE_FINISH,
  finish_left: EDGE_FINISH,
  finish_right: EDGE_FINISH,
  ring_position: {
    corners: t(
      'Een ring in elke hoek. Genoeg voor kleinere formaten en snelle montage.',
      'One eyelet in each corner. Enough for smaller sizes and quick mounting.',
      'Eine Öse in jeder Ecke. Ausreichend für kleinere Formate und schnelle Montage.',
      'Un œillet dans chaque coin. Suffisant pour les petits formats et un montage rapide.',
    ),
  },
  all_sides_equal_finish: {
    yes: t(
      'Alle zijden krijgen dezelfde afwerking. Je kiest de afwerking één keer.',
      'All sides get the same finish. You choose the finish once.',
      'Alle Seiten erhalten die gleiche Verarbeitung. Sie wählen sie nur einmal.',
      'Tous les côtés ont la même finition. Vous la choisissez une seule fois.',
    ),
    no: t(
      'Per zijde verschillend: je kiest voor boven, onder, links en rechts een eigen afwerking, bijvoorbeeld een tunnelzoom boven en onder.',
      'Different per side: choose a separate finish for top, bottom, left and right, for example pole pockets at top and bottom.',
      'Je Seite unterschiedlich: Sie wählen für oben, unten, links und rechts eine eigene Verarbeitung, etwa Hohlsäume oben und unten.',
      'Différente par côté : choisissez une finition pour le haut, le bas, la gauche et la droite, par exemple des fourreaux en haut et en bas.',
    ),
  },
  application_side: {
    outside: t(
      'De sticker wordt aan de buitenkant geplakt. Standaardkeuze voor muren, panelen, deuren en ramen waar je aan de buitenzijde plakt.',
      'The sticker is applied on the outside. Standard choice for walls, panels, doors and windows when applying to the outer surface.',
      'Der Aufkleber wird außen angebracht. Standardwahl für Wände, Platten, Türen und Fenster, wenn außen geklebt wird.',
      'Le sticker est posé à l’extérieur. Choix standard pour murs, panneaux, portes et vitres posés côté extérieur.',
    ),
    inside: t(
      'De sticker wordt aan de binnenkant van glas geplakt en van buitenaf bekeken. Lever je ontwerp gespiegeld aan, anders staat tekst verkeerd om.',
      'The sticker is applied to the inside of glass and viewed from outside. Supply your design mirrored, otherwise text will be reversed.',
      'Der Aufkleber wird innen auf Glas angebracht und von außen betrachtet. Liefern Sie Ihr Design gespiegelt, sonst steht Text verkehrt herum.',
      'Le sticker est posé à l’intérieur de la vitre et vu de l’extérieur. Fournissez votre design en miroir, sinon le texte sera inversé.',
    ),
  },
  individually_sealed: {
    no: NOT_SEALED,
    none: NOT_SEALED,
    null: NOT_SEALED,
    yes: t(
      'Elk exemplaar wordt apart in folie geseald. Handig voor doorverkoop, cadeaus of om het schoon en droog te bewaren.',
      'Each item is sealed separately in film. Handy for resale, gifts or keeping it clean and dry.',
      'Jedes Exemplar wird einzeln in Folie eingeschweißt. Praktisch für den Wiederverkauf, Geschenke oder saubere, trockene Lagerung.',
      'Chaque exemplaire est emballé individuellement sous film. Pratique pour la revente, les cadeaux ou un stockage propre et sec.',
    ),
  },
  sealed: {
    none: t(
      'Niet aftellen en sealen. De producten worden samen verpakt.',
      'Not counted and sealed. The products are packed together.',
      'Nicht abgezählt und eingeschweißt. Die Produkte werden zusammen verpackt.',
      'Pas de comptage ni de mise sous film. Les produits sont emballés ensemble.',
    ),
    with_sleeve: t(
      'Verpakt met een plastic sealrand.',
      'Packed with a plastic seal edge.',
      'Verpackt mit einem Kunststoff-Siegelrand.',
      'Emballé avec un bord scellé en plastique.',
    ),
  },
  squarefold: {
    yes: t(
      'Squarefold: de rug van een geniete brochure wordt platgeperst tot een vierkante rug. Het boekje ligt vlakker en oogt als gelijmd.',
      'Squarefold: the spine of a stapled booklet is pressed flat into a square spine. The booklet lies flatter and looks perfect bound.',
      'Squarefold: Der Rücken einer gehefteten Broschüre wird zu einem eckigen Rücken gepresst. Sie liegt flacher und wirkt wie klebegebunden.',
      'Squarefold : le dos d’une brochure agrafée est pressé en dos carré. Elle reste plus à plat et ressemble à un dos collé.',
    ),
    no: t(
      'Geen squarefold: de geniete rug blijft rond, zoals gebruikelijk.',
      'No squarefold: the stapled spine stays rounded, as usual.',
      'Kein Squarefold: Der geheftete Rücken bleibt rund, wie üblich.',
      'Pas de squarefold : le dos agrafé reste arrondi, comme d’habitude.',
    ),
  },
  laminate: {
    none: NO_LAMINATE,
    'no-laminate': NO_LAMINATE,
    gloss: GLOSS_LAMINATE,
    'gloss-lam': GLOSS_LAMINATE,
    matt: MATT_LAMINATE,
  },
  'block-out-foil': {
    yes: t(
      'Blockout folie: een lichtdichte laag, zodat de achterkant of ondergrond niet door de print heen schijnt.',
      'Blockout film: a light-blocking layer, so the back or background does not show through the print.',
      'Blockout-Folie: eine lichtundurchlässige Schicht, damit Rückseite oder Untergrund nicht durch den Druck scheinen.',
      'Film blockout : une couche opaque, pour que le verso ou le support ne transparaisse pas à travers l’impression.',
    ),
    no: t(
      'Zonder blockout folie. Bij tegenlicht kan de achterkant of ondergrond door de print heen schijnen.',
      'No blockout film. With backlight, the back or background may show through the print.',
      'Ohne Blockout-Folie. Bei Gegenlicht kann Rückseite oder Untergrund durch den Druck scheinen.',
      'Sans film blockout. En contre-jour, le verso ou le support peut transparaître à travers l’impression.',
    ),
    none: t(
      'Zonder blockout folie. Bij tegenlicht kan de achterkant of ondergrond door de print heen schijnen.',
      'No blockout film. With backlight, the back or background may show through the print.',
      'Ohne Blockout-Folie. Bei Gegenlicht kann Rückseite oder Untergrund durch den Druck scheinen.',
      'Sans film blockout. En contre-jour, le verso ou le support peut transparaître à travers l’impression.',
    ),
  },
  fold_out_finish_cover: {
    none: NO_FLAP,
    fold_out_cover_cut_2mm_back: t(
      'Terugliggend: de flap is 2 mm smaller dan de pagina, zodat hij soepel omslaat en niet uitsteekt.',
      'Set back: the flap is 2 mm narrower than the page, so it folds smoothly and does not stick out.',
      'Zurückgesetzt: Die Klappe ist 2 mm schmaler als die Seite, damit sie leicht umschlägt und nicht übersteht.',
      'En retrait : le rabat est 2 mm plus étroit que la page pour se replier facilement sans dépasser.',
    ),
    fold_out_cover_cut_evenly: t(
      'Gelijk af: de flap is even breed als de pagina en sluit strak aan.',
      'Flush: the flap is as wide as the page and lines up exactly.',
      'Bündig: Die Klappe ist genauso breit wie die Seite und schließt exakt ab.',
      'À fleur : le rabat a la même largeur que la page et s’aligne exactement.',
    ),
  },
  cover_fold_out: {
    none: NO_FLAP,
    front: t(
      'Flap aan de voorzijde van de omslag: een extra uitvouwbaar paneel voor meer informatie of een opvallend ontwerp.',
      'Flap on the front cover: an extra fold-out panel for more information or an eye-catching design.',
      'Klappe am vorderen Umschlag: eine zusätzliche ausklappbare Fläche für mehr Informationen oder ein auffälliges Design.',
      'Rabat sur la couverture avant : un volet dépliable en plus pour davantage d’informations ou un design marquant.',
    ),
    back: t(
      'Flap aan de achterzijde van de omslag: een extra uitvouwbaar paneel voor meer informatie of een opvallend ontwerp.',
      'Flap on the back cover: an extra fold-out panel for more information or an eye-catching design.',
      'Klappe am hinteren Umschlag: eine zusätzliche ausklappbare Fläche für mehr Informationen oder ein auffälliges Design.',
      'Rabat sur la couverture arrière : un volet dépliable en plus pour davantage d’informations ou un design marquant.',
    ),
    front_and_back: t(
      'Flappen aan de voor- en achterzijde van de omslag: twee extra uitvouwbare panelen voor meer informatie of ontwerp.',
      'Flaps on the front and back cover: two extra fold-out panels for more information or design.',
      'Klappen am vorderen und hinteren Umschlag: zwei zusätzliche ausklappbare Flächen für mehr Informationen oder Design.',
      'Rabats sur les couvertures avant et arrière : deux volets dépliables en plus pour informations ou design.',
    ),
  },
  packaging_extras: {
    packaging_handling: PACKED,
    packaging_vinyl: PACKED,
    packaging_wallpaper: PACKED,
    packaging_canvas: PACKED,
    packaging_banner: PACKED,
    packaging_textile: PACKED,
    packaging_plate: PACKED,
    packaging_accessories: PACKED,
    none: t(
      'Geen extra verpakking.',
      'No extra packaging.',
      'Keine zusätzliche Verpackung.',
      'Pas d’emballage supplémentaire.',
    ),
    'roll-up_packaging': t(
      'Verpakking voor de roll-up, om hem te vervoeren en op te bergen.',
      'Packaging for the roll-up, to transport and store it.',
      'Verpackung für das Roll-up, zum Transportieren und Aufbewahren.',
      'Emballage pour le roll-up, pour le transporter et le ranger.',
    ),
    wrapped_sealing: t(
      'Per 100 stuks verpakt in krimpfolie.',
      'Packed per 100 pieces in shrink film.',
      'Je 100 Stück in Schrumpffolie verpackt.',
      'Emballé par 100 pièces sous film rétractable.',
    ),
    none_sealing: t(
      'Niet in folie geseald.',
      'Not sealed in film.',
      'Nicht in Folie eingeschweißt.',
      'Pas d’emballage sous film.',
    ),
    no_stirrers: t(
      'Zonder roerstaafjes.',
      'Without stirrers.',
      'Ohne Rührstäbchen.',
      'Sans bâtonnets mélangeurs.',
    ),
    plano_sheet_package: t(
      'Geleverd als pakket ongevouwen vellen.',
      'Supplied as a pack of flat, unfolded sheets.',
      'Geliefert als Paket ungefalzter Bögen.',
      'Livré en paquet de feuilles à plat, non pliées.',
    ),
    inlay_card: t(
      'Inlegkaart van 300 grams karton, dubbelzijdig bedrukt (180 x 90 mm).',
      'Insert card on 300 gsm board, printed on both sides (180 x 90 mm).',
      'Einlegekarte aus 300 g Karton, beidseitig bedruckt (180 x 90 mm).',
      'Carte d’insertion en carton 300 g, imprimée recto verso (180 x 90 mm).',
    ),
    '30-degrees': t(
      'Wasadvies: wassen op 30 graden, niet strijken en niet in de droger.',
      'Care advice: wash at 30 degrees, do not iron and do not tumble dry.',
      'Pflegehinweis: bei 30 Grad waschen, nicht bügeln und nicht in den Trockner.',
      'Conseil d’entretien : lavage à 30 degrés, ne pas repasser, pas de sèche-linge.',
    ),
    '250_pcs_box_25_pcs_seal': t(
      '250 stuks per doos, per 25 stuks strak in folie geseald.',
      '250 pieces per box, sealed tightly in film per 25 pieces.',
      '250 Stück pro Karton, je 25 Stück straff in Folie eingeschweißt.',
      '250 pièces par carton, scellées sous film par 25 pièces.',
    ),
    including_batteries: t(
      'Inclusief 2 LR44-knoopcelbatterijen.',
      'Includes 2 LR44 button cell batteries.',
      'Inklusive 2 LR44-Knopfzellen.',
      'Avec 2 piles bouton LR44.',
    ),
    deposit: t(
      'Statiegeld van € 0,15 per stuk zit al in de prijs. Kent jouw land dit systeem niet? Mail ons, dan betalen we het statiegeld terug.',
      'A deposit of € 0.15 per item is included in the price. If your country has no deposit system, email us and we will refund it.',
      'Ein Pfand von 0,15 € pro Stück ist im Preis enthalten. Gibt es in Ihrem Land kein Pfandsystem, schreiben Sie uns und wir erstatten es.',
      'Une consigne de 0,15 € par pièce est incluse dans le prix. Si votre pays n’a pas ce système, écrivez-nous et nous la rembourserons.',
    ),
    resealable_envelope: t(
      'Hersluitbare envelop in je eigen ontwerp, aan één zijde in full colour bedrukt.',
      'Resealable envelope with your own design, printed in full colour on one side.',
      'Wiederverschließbarer Umschlag mit Ihrem eigenen Design, einseitig vierfarbig bedruckt.',
      'Enveloppe refermable à votre design, imprimée en quadrichromie sur une face.',
    ),
    white_envelope: t(
      'Met een onbedrukte witte A5-envelop.',
      'With a blank white A5 envelope.',
      'Mit einem unbedruckten weißen A5-Umschlag.',
      'Avec une enveloppe A5 blanche non imprimée.',
    ),
    expiry_date: t(
      'Ongeveer 12 maanden houdbaar.',
      'Shelf life of about 12 months.',
      'Etwa 12 Monate haltbar.',
      'Se conserve environ 12 mois.',
    ),
    white_box: t(
      'Verpakt in een witte doos.',
      'Packed in a white box.',
      'In einem weißen Karton verpackt.',
      'Emballé dans une boîte blanche.',
    ),
    dispenser_box: t(
      'Verpakt in een dispenserdoos, waaruit je de producten één voor één pakt.',
      'Packed in a dispenser box, so you can take the products out one by one.',
      'In einer Spenderbox verpackt, aus der Sie die Produkte einzeln entnehmen.',
      'Emballé dans une boîte distributrice pour prendre les produits un par un.',
    ),
    brown_dispenser_box: t(
      'Verpakt in een bruine dispenserdoos, waaruit je de producten één voor één pakt.',
      'Packed in a brown dispenser box, so you can take the products out one by one.',
      'In einer braunen Spenderbox verpackt, aus der Sie die Produkte einzeln entnehmen.',
      'Emballé dans une boîte distributrice brune pour prendre les produits un par un.',
    ),
  },
  closure_wristband: {
    'slidingclosure_one-off': t(
      'Schuifsluiting voor eenmalig gebruik: eenmaal dicht gaat het bandje alleen af door het door te knippen. Zo is het niet over te dragen.',
      'One-time sliding closure: once closed, the band can only be removed by cutting it, so it cannot be passed on.',
      'Schiebeverschluss zur einmaligen Nutzung: Einmal geschlossen, lässt sich das Band nur durch Aufschneiden entfernen. So ist es nicht übertragbar.',
      'Fermeture coulissante à usage unique : une fois fermé, le bracelet ne s’enlève qu’en le coupant. Il n’est donc pas transmissible.',
    ),
    'clipclosure_one-off': ONE_OFF_CLIP,
    plastic_clipclosure: ONE_OFF_CLIP,
    adjustable_closure_reusable: t(
      'Verstelbare sluiting: het bandje kan open en weer dicht, dus je kunt het opnieuw gebruiken.',
      'Adjustable closure: the band can be opened and closed again, so it is reusable.',
      'Verstellbarer Verschluss: Das Band lässt sich öffnen und wieder schließen und ist daher wiederverwendbar.',
      'Fermeture réglable : le bracelet s’ouvre et se referme, il est donc réutilisable.',
    ),
    'self-adhesive_closure': t(
      'Zelfklevende sluiting: je plakt de uiteinden op elkaar. Snel aan te brengen en bedoeld voor eenmalig gebruik.',
      'Self-adhesive closure: stick the ends together. Quick to apply and intended for one-time use.',
      'Selbstklebender Verschluss: Die Enden werden zusammengeklebt. Schnell angelegt und für die einmalige Nutzung gedacht.',
      'Fermeture autocollante : collez les extrémités ensemble. Rapide à poser et prévu pour un usage unique.',
    ),
  },
  // Overgenomen uit de configurator (visitekaartjes en folders).
  plano_delivery: {
    yes: t(
      'Plano geleverd: ongevouwen, handig als je zelf wilt vouwen of nabewerken.',
      'Delivered flat: unfolded, handy if you want to fold or finish it yourself.',
      'Plano geliefert: ungefalzt, praktisch, wenn Sie selbst falzen oder weiterverarbeiten möchten.',
      'Livré à plat : non plié, pratique si vous voulez plier ou façonner vous-même.',
    ),
    no: t(
      'Niet plano geleverd. Het product komt volgens de gekozen afwerking, bijvoorbeeld gevouwen.',
      'Not delivered flat. The product arrives with the chosen finish, for example folded.',
      'Nicht plano geliefert. Das Produkt kommt mit der gewählten Verarbeitung, zum Beispiel gefalzt.',
      'Pas livré à plat. Le produit arrive avec la finition choisie, par exemple plié.',
    ),
    null: t(
      'Geen plano levering als extra optie. Dit is de standaardkeuze.',
      'No flat delivery as an extra option. This is the standard choice.',
      'Keine Planolieferung als Zusatzoption. Dies ist die Standardwahl.',
      'Pas de livraison à plat en option. C’est le choix standard.',
    ),
    none: t(
      'Geen plano levering als extra optie. Dit is de standaardkeuze.',
      'No flat delivery as an extra option. This is the standard choice.',
      'Keine Planolieferung als Zusatzoption. Dies ist die Standardwahl.',
      'Pas de livraison à plat en option. C’est le choix standard.',
    ),
  },
  bundle: {
    none: t(
      'Niet aftellen en bundelen. Je ontvangt de producten zonder extra bundelbewerking.',
      'Not counted and bundled. You receive the products without extra bundling.',
      'Nicht abgezählt und gebündelt. Sie erhalten die Produkte ohne zusätzliche Bündelung.',
      'Pas de comptage ni de liasses. Vous recevez les produits sans regroupement.',
    ),
  },
  loop_stitches: {
    none: t(
      'Geen oognieten.',
      'No loop stitches.',
      'Keine Ösenheftung.',
      'Pas d’agrafes à œillets.',
    ),
    '2_loop_stitches': t(
      'Twee oognieten op de rug, zodat je het drukwerk in een ringband kunt bewaren.',
      'Two loop stitches on the spine, so you can keep the print in a ring binder.',
      'Zwei Ösenklammern am Rücken, damit Sie das Druckwerk in einem Ringordner abheften können.',
      'Deux agrafes à œillets au dos, pour ranger l’imprimé dans un classeur à anneaux.',
    ),
  },
  finish: {
    geen: t(
      'Geen extra beschermlaag of laminaat. De meest voordelige keuze.',
      'No extra protective layer or laminate. The most economical choice.',
      'Keine zusätzliche Schutzschicht oder Laminierung. Die günstigste Wahl.',
      'Pas de couche de protection ni de pelliculage. Le choix le plus économique.',
    ),
    single_sided_gloss_lamination: t(
      'Glanzende beschermlaag aan de buitenzijde. Geeft meer glans en beschermt tegen lichte slijtage.',
      'Gloss protective layer on the outside. Adds shine and protects against light wear.',
      'Glänzende Schutzschicht auf der Außenseite. Mehr Glanz und Schutz vor leichtem Verschleiß.',
      'Pelliculage brillant à l’extérieur. Plus d’éclat et une protection contre l’usure légère.',
    ),
    single_sided_matt_lamination: t(
      'Matte beschermlaag aan de buitenzijde. Rustige uitstraling met minder reflectie.',
      'Matt protective layer on the outside. A calm look with less reflection.',
      'Matte Schutzschicht auf der Außenseite. Ruhige Optik mit weniger Reflexion.',
      'Pelliculage mat à l’extérieur. Un aspect sobre avec moins de reflets.',
    ),
    double_sided_gloss_lamination: t(
      'Glanzende beschermlaag aan beide zijden. Geschikt als beide kanten zichtbaar zijn en veel gebruikt worden.',
      'Gloss protective layer on both sides. Suitable when both sides are visible and handled a lot.',
      'Glänzende Schutzschicht auf beiden Seiten. Geeignet, wenn beide Seiten sichtbar sind und viel genutzt werden.',
      'Pelliculage brillant recto verso. Adapté si les deux faces sont visibles et très manipulées.',
    ),
    double_sided_matt_lamination: t(
      'Matte beschermlaag aan beide zijden. Professionele uitstraling met minder reflectie.',
      'Matt protective layer on both sides. A professional look with less reflection.',
      'Matte Schutzschicht auf beiden Seiten. Professionelle Optik mit weniger Reflexion.',
      'Pelliculage mat recto verso. Un aspect professionnel avec moins de reflets.',
    ),
    single_soft_touch_lamination: t(
      'Zachte matte beschermlaag aan één zijde. Voelt luxe aan als je het vasthoudt.',
      'Soft matt protective layer on one side. Feels luxurious in your hand.',
      'Weiche matte Schutzschicht auf einer Seite. Fühlt sich in der Hand edel an.',
      'Pelliculage mat toucher doux sur une face. Agréable et luxueux au toucher.',
    ),
    double_soft_touch_lamination: t(
      'Zachte matte beschermlaag aan beide zijden. Premium keuze voor representatief drukwerk.',
      'Soft matt protective layer on both sides. A premium choice for representative print.',
      'Weiche matte Schutzschicht auf beiden Seiten. Premium-Wahl für repräsentative Drucksachen.',
      'Pelliculage mat toucher doux recto verso. Un choix premium pour des imprimés de prestige.',
    ),
    single_sided_scratch_resistant_laminate: t(
      'Extra krasvaste matte beschermlaag aan één zijde.',
      'Extra scratch-resistant matt protective layer on one side.',
      'Besonders kratzfeste matte Schutzschicht auf einer Seite.',
      'Pelliculage mat anti-rayures sur une face.',
    ),
    double_sided_scratch_resistant_laminate: t(
      'Extra krasvaste matte beschermlaag aan beide zijden.',
      'Extra scratch-resistant matt protective layer on both sides.',
      'Besonders kratzfeste matte Schutzschicht auf beiden Seiten.',
      'Pelliculage mat anti-rayures recto verso.',
    ),
  },
};

// sticking_side gebruikt dezelfde waarden als application_side.
EXACT.sticking_side = EXACT.application_side;

// ---------------------------------------------------------------------------
// Patroonregels per optie
// ---------------------------------------------------------------------------

type PatternRule = (value: string, lang: OptionDescriptionLocale) => string | null;

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

const creasingLinePattern: PatternRule = (value, lang) => {
  if (!/^[1-9]\d?$/.test(value)) return null;
  const n = Number(value);
  return t(
    `${n} ${plural(n, 'rillijn', 'rillijnen')}: een ingedrukte vouwlijn, zodat het materiaal strak en zonder barsten vouwt. Houd in je ontwerp rekening met de plek van de ${plural(n, 'vouw', 'vouwen')}.`,
    `${n} ${plural(n, 'crease line', 'crease lines')}: a pressed fold line so the material folds neatly without cracking. Keep the fold ${plural(n, 'position', 'positions')} in mind in your design.`,
    `${n} ${plural(n, 'Rilllinie', 'Rilllinien')}: eine eingedrückte Falzlinie, damit das Material sauber und ohne Bruch faltet. Berücksichtigen Sie die Falzposition in Ihrem Design.`,
    `${n} ${plural(n, 'rainure', 'rainures')} : une ligne de pliage marquée pour que le support se plie proprement sans casser. Tenez compte de l’emplacement des plis dans votre design.`,
  )[lang];
};

const perforationPattern: PatternRule = (value, lang) => {
  const m = value.match(/^(\d+)_perforation_lines?(_raffle)?$/);
  if (!m) return null;
  const n = Number(m[1]);
  if (m[2]) {
    return t(
      `${n} ${plural(n, 'perforatielijn', 'perforatielijnen')} voor loten: je scheurt het lot netjes los van het deel dat je zelf bewaart.`,
      `${n} ${plural(n, 'perforation line', 'perforation lines')} for raffle tickets: the ticket tears neatly from the stub you keep.`,
      `${n} ${plural(n, 'Perforationslinie', 'Perforationslinien')} für Lose: Das Los lässt sich sauber vom Abschnitt abtrennen, den Sie behalten.`,
      `${n} ${plural(n, 'ligne de perforation', 'lignes de perforation')} pour billets de tombola : le billet se détache proprement de la souche que vous gardez.`,
    )[lang];
  }
  return t(
    `${n} ${plural(n, 'perforatielijn', 'perforatielijnen')}: een rij kleine gaatjes waarlangs je een deel netjes afscheurt, bijvoorbeeld een bon of antwoordstrook. Geef de positie aan in je ontwerp.`,
    `${n} ${plural(n, 'perforation line', 'perforation lines')}: a row of small holes to tear off a part neatly, such as a coupon or reply slip. Mark the position in your design.`,
    `${n} ${plural(n, 'Perforationslinie', 'Perforationslinien')}: eine Reihe kleiner Löcher, um einen Teil sauber abzutrennen, etwa einen Gutschein oder Antwortabschnitt. Markieren Sie die Position im Design.`,
    `${n} ${plural(n, 'ligne de perforation', 'lignes de perforation')} : une rangée de petits trous pour détacher proprement une partie, comme un bon ou un coupon-réponse. Indiquez la position dans votre design.`,
  )[lang];
};

const roundedCornersPattern: PatternRule = (value, lang) => {
  const m = value.match(/^(\d+(?:[.,]\d+)?)_mm_radius$/);
  if (!m) return null;
  const r = Number(m[1].replace(',', '.'));
  const shown = num(m[1], lang);
  const level = r <= 4 ? 0 : r <= 10 ? 1 : 2;
  const feel = [
    t('subtiel afgeronde hoeken', 'subtly rounded corners', 'dezent abgerundete Ecken', 'des coins légèrement arrondis'),
    t('duidelijk afgeronde hoeken', 'clearly rounded corners', 'deutlich abgerundete Ecken', 'des coins nettement arrondis'),
    t('sterk afgeronde hoeken', 'strongly rounded corners', 'stark abgerundete Ecken', 'des coins très arrondis'),
  ][level][lang];
  return t(
    `Hoeken afgerond met een radius van ${shown} mm. Hoe groter de radius, hoe ronder de hoek. Dit geeft ${feel}.`,
    `Corners rounded with a ${shown} mm radius. The larger the radius, the rounder the corner. This gives ${feel}.`,
    `Ecken mit einem Radius von ${shown} mm abgerundet. Je größer der Radius, desto runder die Ecke. Das ergibt ${feel}.`,
    `Coins arrondis avec un rayon de ${shown} mm. Plus le rayon est grand, plus le coin est rond. Cela donne ${feel}.`,
  )[lang];
};

const drillholesPattern: PatternRule = (value, lang) => {
  let m = value.match(/^drillholes_corner_(\d+(?:\.\d+)?)mm$/);
  if (m) {
    const d = num(m[1], lang);
    return t(
      `Vier boorgaten in de hoeken met een diameter van ${d} mm. Deze tonen we automatisch in de preview.`,
      `Four corner drill holes with a ${d} mm diameter. These are shown automatically in the preview.`,
      `Vier Bohrlöcher in den Ecken mit ${d} mm Durchmesser. Diese zeigen wir automatisch in der Vorschau.`,
      `Quatre trous dans les coins avec un diamètre de ${d} mm. Ils sont affichés automatiquement dans l’aperçu.`,
    )[lang];
  }
  m = value.match(/^drillholes_(\d+)_cm$/);
  if (m) {
    const cm = m[1];
    return t(
      `Voorgeboord met een gat om de ${cm} cm, zodat je het product direct kunt vastschroeven.`,
      `Pre-drilled with a hole every ${cm} cm, so you can screw the product in place right away.`,
      `Vorgebohrt mit einem Loch alle ${cm} cm, damit Sie das Produkt direkt festschrauben können.`,
      `Pré-percé avec un trou tous les ${cm} cm, pour visser le produit directement.`,
    )[lang];
  }
  m = value.match(/^([a-z]+)_drillholes?_custom$/);
  if (m && COUNT_WORDS[m[1]]) {
    const n = COUNT_WORDS[m[1]];
    return t(
      `${n} ${plural(n, 'boorgat', 'boorgaten')} op een plek naar keuze. Zet de boorgaten of duidelijke boormarkeringen zelf in je printbestand, anders weten we niet waar je ze wilt.`,
      `${n} ${plural(n, 'drill hole', 'drill holes')} at positions of your choice. Add the holes or clear drill marks to your print file, otherwise we do not know where you want them.`,
      `${n} ${plural(n, 'Bohrloch', 'Bohrlöcher')} an Positionen Ihrer Wahl. Setzen Sie die Löcher oder klare Bohrmarkierungen selbst in die Druckdatei, sonst wissen wir nicht, wo Sie sie möchten.`,
      `${n} ${plural(n, 'trou', 'trous')} à la position de votre choix. Ajoutez les trous ou des repères clairs dans votre fichier, sinon nous ne savons pas où les placer.`,
    )[lang];
  }
  m = value.match(/^([a-z]+)_drillholes?_6mm_80_80$/);
  if (m && COUNT_WORDS[m[1]]) {
    const n = COUNT_WORDS[m[1]];
    return t(
      `${n} ${plural(n, 'boorgat', 'boorgaten')} van 6 mm, met 80 mm tussen de middelpunten van de gaten.`,
      `${n} ${plural(n, 'drill hole', 'drill holes')} of 6 mm, with 80 mm between the hole centres.`,
      `${n} ${plural(n, 'Bohrloch', 'Bohrlöcher')} von 6 mm, mit 80 mm Abstand zwischen den Lochmitten.`,
      `${n} ${plural(n, 'trou', 'trous')} de 6 mm, avec 80 mm d’entraxe.`,
    )[lang];
  }
  m = value.match(/^([a-z]+)_drillholes?(?:_(\d+(?:\.\d+)?)mm)?$/);
  if (m && COUNT_WORDS[m[1]]) {
    const n = COUNT_WORDS[m[1]];
    const d = num(m[2] || '6', lang);
    return t(
      `${n} ${plural(n, 'boorgat', 'boorgaten')} van ${d} mm, bijvoorbeeld om op te hangen aan een koord, haakje of schroef.`,
      `${n} ${plural(n, 'drill hole', 'drill holes')} of ${d} mm, for example to hang it on a cord, hook or screw.`,
      `${n} ${plural(n, 'Bohrloch', 'Bohrlöcher')} von ${d} mm, zum Beispiel zum Aufhängen an einer Schnur, einem Haken oder einer Schraube.`,
      `${n} ${plural(n, 'trou', 'trous')} de ${d} mm, par exemple pour accrocher à une cordelette, un crochet ou une vis.`,
    )[lang];
  }
  if (value.includes('custom')) {
    return t(
      'Boorgaten op een plek naar keuze. Zet de boorgaten of duidelijke boormarkeringen zelf in je printbestand, anders weten we niet waar je ze wilt.',
      'Drill holes at positions of your choice. Add the holes or clear drill marks to your print file, otherwise we do not know where you want them.',
      'Bohrlöcher an Positionen Ihrer Wahl. Setzen Sie die Löcher oder klare Bohrmarkierungen selbst in die Druckdatei, sonst wissen wir nicht, wo Sie sie möchten.',
      'Trous à la position de votre choix. Ajoutez les trous ou des repères clairs dans votre fichier, sinon nous ne savons pas où les placer.',
    )[lang];
  }
  return null;
};

const contourLinesPattern: PatternRule = (value, lang) => {
  if (['none', 'no_contour_lines', '00', '0'].includes(value)) {
    return t(
      'Geen aparte contourlijnen. Er worden geen extra losse vormen uit je ontwerp gesneden.',
      'No separate contour lines. No extra individual shapes are cut from your design.',
      'Keine separaten Konturlinien. Es werden keine zusätzlichen Einzelformen aus Ihrem Design geschnitten.',
      'Pas de lignes de contour séparées. Aucune forme individuelle supplémentaire n’est découpée.',
    )[lang];
  }
  let m = value.match(/^(\d+)_(\d+)$/);
  if (m) {
    const [from, to] = [m[1], m[2]];
    return t(
      `${from} tot ${to} contourlijnen: zoveel losse vormen worden uit je ontwerp gesneden. Zorg dat elke vorm een eigen snijlijn heeft in je bestand.`,
      `${from} to ${to} contour lines: that many separate shapes are cut from your design. Make sure each shape has its own cut line in your file.`,
      `${from} bis ${to} Konturlinien: So viele einzelne Formen werden aus Ihrem Design geschnitten. Jede Form braucht eine eigene Schnittlinie in Ihrer Datei.`,
      `${from} à ${to} lignes de contour : autant de formes séparées sont découpées dans votre design. Chaque forme doit avoir sa propre ligne de découpe.`,
    )[lang];
  }
  m = value.match(/^(\d+)$/);
  if (m) {
    const n = Number(m[1]);
    return t(
      `${n} ${plural(n, 'contourlijn', 'contourlijnen')}: zoveel losse ${plural(n, 'vorm wordt', 'vormen worden')} uit je ontwerp gesneden. Zorg dat je bestand precies zoveel snijlijnen bevat.`,
      `${n} ${plural(n, 'contour line', 'contour lines')}: ${plural(n, 'one shape is', `${n} separate shapes are`)} cut from your design. Make sure your file contains exactly that many cut lines.`,
      `${n} ${plural(n, 'Konturlinie', 'Konturlinien')}: So viele einzelne Formen werden aus Ihrem Design geschnitten. Ihre Datei sollte genau so viele Schnittlinien enthalten.`,
      `${n} ${plural(n, 'ligne de contour', 'lignes de contour')} : autant de formes séparées sont découpées dans votre design. Votre fichier doit contenir exactement ce nombre de lignes de découpe.`,
    )[lang];
  }
  return null;
};

const hotfoilPattern: PatternRule = (value, lang) => {
  const m = value.match(/^(\d)(\d)$/);
  if (!m) return null;
  const front = Number(m[1]);
  const back = Number(m[2]);
  const frontText = t(
    `${front} ${plural(front, 'hotfoilkleur', 'hotfoilkleuren')} aan de voorkant`,
    `${front} hot foil ${plural(front, 'colour', 'colours')} on the front`,
    `${front} ${plural(front, 'Heißfolienfarbe', 'Heißfolienfarben')} auf der Vorderseite`,
    `${front} ${plural(front, 'couleur', 'couleurs')} de dorure à chaud au recto`,
  )[lang];
  const backText = back === 0
    ? t('en geen aan de achterkant', 'and none on the back', 'und keine auf der Rückseite', 'et aucune au verso')[lang]
    : t(
      `en ${back} aan de achterkant`,
      `and ${back} on the back`,
      `und ${back} auf der Rückseite`,
      `et ${back} au verso`,
    )[lang];
  return t(
    `${m[1]}/${m[2]} hotfoil: ${frontText} ${backText}. Metallic folie die met warmte en druk wordt aangebracht.`,
    `${m[1]}/${m[2]} hot foil: ${frontText} ${backText}. Metallic foil applied with heat and pressure.`,
    `${m[1]}/${m[2]} Heißfolie: ${frontText} ${backText}. Metallicfolie, die mit Wärme und Druck aufgebracht wird.`,
    `Dorure à chaud ${m[1]}/${m[2]} : ${frontText} ${backText}. Un film métallisé appliqué par chaleur et pression.`,
  )[lang];
};

const foilPrintPattern: PatternRule = (value, lang) =>
  value.startsWith('letterpress_foil_') ? LETTERPRESS_FOIL[lang] : null;

const sealedPattern: PatternRule = (value, lang) => {
  if (!/^\d+$/.test(value)) return null;
  return t(
    `Afgeteld en per ${value} stuks in folie geseald. Handig om uit te delen of op voorraad te leggen.`,
    `Counted and sealed in film per ${value} pieces. Handy for handing out or keeping in stock.`,
    `Abgezählt und je ${value} Stück in Folie eingeschweißt. Praktisch zum Verteilen oder zur Lagerung.`,
    `Comptés et emballés sous film par ${value} pièces. Pratique pour distribuer ou stocker.`,
  )[lang];
};

const bundlePattern: PatternRule = (value, lang) => {
  const m = value.match(/^bundle_per_(\d+)$/);
  if (!m) return null;
  return t(
    `De producten worden per ${m[1]} stuks gebundeld. Handig als je sets wilt uitdelen of intern verdelen.`,
    `The products are bundled per ${m[1]} pieces. Handy for handing out sets or distributing internally.`,
    `Die Produkte werden je ${m[1]} Stück gebündelt. Praktisch, um Sets auszugeben oder intern zu verteilen.`,
    `Les produits sont regroupés par ${m[1]} pièces. Pratique pour distribuer des lots ou répartir en interne.`,
  )[lang];
};

const ringPositionPattern: PatternRule = (value, lang) => {
  const m = value.match(/^every_(\d+)$/);
  if (!m) return null;
  return t(
    `Ringen om de ${m[1]} cm. Hoe kleiner de afstand, hoe meer bevestigingspunten en hoe strakker het doek hangt.`,
    `Eyelets every ${m[1]} cm. The smaller the spacing, the more fixing points and the tighter the material hangs.`,
    `Ösen alle ${m[1]} cm. Je kleiner der Abstand, desto mehr Befestigungspunkte und desto straffer hängt das Material.`,
    `Œillets tous les ${m[1]} cm. Plus l’écart est petit, plus il y a de points de fixation et plus la bâche est tendue.`,
  )[lang];
};

const foldPattern: PatternRule = (value, lang) => {
  const m = value.match(/^(wikkel|zigzag)_(\d+)$/);
  if (!m) return null;
  const pages = Number(m[2]);
  const folds = pages / 2 - 1;
  if (m[1] === 'wikkel') {
    return t(
      `Wikkelvouw met ${pages} pagina’s: ${folds} vouwen in dezelfde richting, de panelen worden naar binnen gewikkeld.`,
      `Roll fold with ${pages} pages: ${folds} folds in the same direction, the panels roll inwards.`,
      `Wickelfalz mit ${pages} Seiten: ${folds} Falze in dieselbe Richtung, die Seiten werden nach innen gewickelt.`,
      `Pli roulé de ${pages} pages : ${folds} plis dans le même sens, les volets s’enroulent vers l’intérieur.`,
    )[lang];
  }
  return t(
    `Zigzagvouw met ${pages} pagina’s: ${folds} vouwen om en om, als een harmonica. Alle panelen zijn makkelijk te openen.`,
    `Zigzag fold with ${pages} pages: ${folds} alternating folds, like an accordion. Every panel is easy to open.`,
    `Zickzackfalz mit ${pages} Seiten: ${folds} abwechselnde Falze, wie eine Ziehharmonika. Alle Seiten lassen sich leicht öffnen.`,
    `Pli accordéon de ${pages} pages : ${folds} plis alternés. Chaque volet s’ouvre facilement.`,
  )[lang];
};

const packagingPattern: PatternRule = (value, lang) => {
  const m = value.match(/^(?:(\d+)_)?wooden_stirrers$/);
  if (!m) return null;
  const n = Number(m[1] || 1);
  return t(
    `Met ${n} ${plural(n, 'pak', 'pakken')} houten roerstaafjes van 1000 stuks.`,
    `With ${n} ${plural(n, 'pack', 'packs')} of 1000 wooden stirrers.`,
    `Mit ${n} ${plural(n, 'Packung', 'Packungen')} Holzrührstäbchen à 1000 Stück.`,
    `Avec ${n} ${plural(n, 'paquet', 'paquets')} de 1000 bâtonnets en bois.`,
  )[lang];
};

const edgeRule: PatternRule = (value, lang) => edgePattern(value, lang);

const PATTERNS: Record<string, PatternRule[]> = {
  variable_creasing_line: [creasingLinePattern],
  perforation: [perforationPattern],
  rounded_corners: [roundedCornersPattern],
  drillholes: [drillholesPattern],
  number_of_contour_lines: [contourLinesPattern],
  hotfoil: [hotfoilPattern],
  spot_finish: [spotFinishPattern],
  spot_finish_back: [spotFinishPattern],
  foil_print_first_color: [foilPrintPattern],
  foil_print_second_color: [foilPrintPattern],
  fold: [foldPattern],
  sealed: [sealedPattern],
  bundle: [bundlePattern],
  ring_position: [ringPositionPattern],
  packaging_extras: [packagingPattern],
  finish_all_around: [edgeRule],
  finish_top: [edgeRule],
  finish_bottom: [edgeRule],
  finish_left: [edgeRule],
  finish_right: [edgeRule],
};

/**
 * Waarden die vroeger in de Nederlandstalige visitekaartjes- en
 * foldertabellen van de configurator stonden. Die tabellen gingen in het
 * Nederlands vóór een opgeslagen omschrijving (vaak Engelse of generieke
 * leverancierstekst). Voor deze waarden blijft dat zo: ook in nl wint de
 * module van de opgeslagen tekst.
 */
const DUTCH_CURATED_VALUES: Record<string, ReadonlySet<string>> = {
  clean_cut: new Set(['yes', 'no', 'null']),
  rounded_corners: new Set(['none', 'null', '3,5_mm_radius', '7_mm_radius']),
  businesscard_creasing: new Set(['yes', 'no', 'null']),
  variable_creasing_line: new Set(['none', 'null', '1', '2', '3', '4', '5']),
  drillholes: new Set([
    'none', 'null', 'one_drillhole_3mm', 'two_drillholes_3mm', 'one_drillhole_4mm', 'two_drillholes_4mm',
    'one_drillhole_5mm', 'two_drillholes_5mm', 'one_drillhole', 'two_drillholes',
  ]),
  die_cut: new Set(['yes', 'no', 'null']),
  perforation: new Set(['none', 'null', '1_perforation_line', '2_perforation_lines']),
  plano_delivery: new Set(['yes', 'no', 'null', 'none']),
  bundle: new Set(['none', 'bundle_per_50', 'bundle_per_100']),
  loop_stitches: new Set(['none', '2_loop_stitches']),
  creasing: new Set(['yes', 'no']),
  folded: new Set(['yes', 'no']),
  finish: new Set(Object.keys(EXACT.finish)),
};

/** True als de moduletekst in het Nederlands vóór een opgeslagen omschrijving gaat. */
export function optionValueDescriptionOverridesStoredDutch(optionId: unknown, value: unknown): boolean {
  const key = String(value === null ? 'null' : value ?? '').trim();
  return Boolean(DUTCH_CURATED_VALUES[String(optionId ?? '').trim()]?.has(key));
}

/** Optie-id's waarvoor deze module teksten heeft. */
export const DESCRIBED_OPTION_IDS: readonly string[] = Array.from(
  new Set([...Object.keys(EXACT), ...Object.keys(PATTERNS)]),
);

/**
 * Klantuitleg voor één optiewaarde, of null als die hier niet beschreven is.
 * `label` wordt nu niet gebruikt, maar staat in de signatuur zodat een
 * toekomstige regel op het label kan terugvallen zonder alle aanroepers te
 * wijzigen.
 */
export function optionValueDescription(
  optionId: unknown,
  value: unknown,
  locale: unknown = 'nl',
  label?: unknown,
): string | null {
  void label;
  const id = String(optionId ?? '').trim();
  if (!id) return null;
  // String(null) is 'null': de oude tabellen kenden die sleutel al.
  const key = String(value === null ? 'null' : value ?? '').trim();
  const lang = normalizeLocale(locale);

  const exact = EXACT[id]?.[key];
  if (exact) return exact[lang];

  for (const rule of PATTERNS[id] || []) {
    const text = rule(key, lang);
    if (text) return text;
  }
  return null;
}
